import React, { useEffect, useMemo, useState, useRef, useCallback } from 'react';
import styled from 'styled-components';
import { FiArrowLeft, FiChevronLeft, FiChevronRight, FiZap, FiRefreshCw } from 'react-icons/fi';
import { authenticatedFetch } from '../utils/api';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';

// Parse timestamps as UTC when no timezone offset is present
const parseTimeUTC = (v) => {
  if (v == null) return NaN;
  if (typeof v === 'number') return v;
  const s = String(v).trim();
  // If already includes timezone (Z or ±HH:MM), trust it
  if (/Z|[+-]\d{2}:?\d{2}$/.test(s)) return new Date(s).getTime();
  // Otherwise treat as UTC by appending Z
  return new Date(s + 'Z').getTime();
};

// Compute timezone offset (ms) for a given UTC ms and IANA time zone
function getTimeZoneOffsetMs(utcMs, timeZone) {
  try {
    const dtf = new Intl.DateTimeFormat('en-US', {
      timeZone,
      hour12: false,
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit'
    });
    const parts = dtf.formatToParts(new Date(utcMs));
    const map = Object.fromEntries(parts.map(p => [p.type, p.value]));
    const y = Number(map.year);
    const m = Number(map.month);
    const d = Number(map.day);
    const h = Number(map.hour);
    const mi = Number(map.minute);
    const se = Number(map.second);
    const asUTC = Date.UTC(y, m - 1, d, h, mi, se);
    return asUTC - utcMs;
  } catch (e) {
    return 0;
  }
}


const PageContainer = styled.div`
  min-height: 100vh;
  background: transparent;
  display: flex;
  flex-direction: column;
  width: 100%;
  height: 100%;
`;

const HeaderBar = styled.div`
  background: linear-gradient(135deg, #2563eb 0%, #1e40af 100%);
  border-bottom: none;
  padding: 20px 40px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.1);
  display: flex;
  align-items: center;
  justify-content: center;
  position: relative;
`;

const PortalTitle = styled.h1`
  color: white;
  font-size: 2.5rem;
  font-weight: 800;
  margin: 0;
  text-shadow: 0 2px 4px rgba(0, 0, 0, 0.3);
  letter-spacing: 1px;
`;

const BackButton = styled.button`
  background: white;
  border: 2px solid #1565c0;
  border-radius: 15px;
  color: #1976d2;
  padding: 15px 25px;
  font-size: 1.1rem;
  font-weight: 600;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 10px;
  transition: all 0.3s ease;
  position: absolute;
  left: 40px;
  top: 50%;
  transform: translateY(-50%);
  box-shadow: 0 4px 15px rgba(255, 255, 255, 0.3);

  &:hover {
    background: #f5f5f5;
    transform: translateY(-50%) translateY(-2px);
    box-shadow: 0 6px 20px rgba(255, 255, 255, 0.4);
  }
`;

const MainContent = styled.div`
  flex: 1;
  padding: 24px 0 40px 0;
  width: 100%;
  overflow-x: hidden;
`;

const ContentWrapper = styled.div`
  width: 100vw;
  margin-left: calc(-50vw + 50%);
  margin-right: calc(-50vw + 50%);
  padding: 0;
`;

const Title = styled.h2`
  color: white;
  display: flex;
  align-items: center;
  gap: 12px;
  margin: 0 0 10px 0;
`;

const ChartContainer = styled.div`
  background: transparent;
  border-radius: 0;
  padding: 0;
  border: none;
  box-shadow: none;
  overflow: visible; /* allow tooltip to render above */
  margin: 0; /* no margins */
  display: block;
  width: 100vw; /* stretch to full viewport width */
  position: relative;
  left: 50%;
  margin-left: -50vw;
  height: auto;
  box-sizing: border-box;
`;

const MetricsContainer = styled.div`
  position: absolute;
  top: 10px;
  right: 20px;
  background: white;
  border: 2px solid #1976d2;
  border-radius: 8px;
  padding: 12px 16px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  z-index: 10;
  width: 200px;
  overflow: visible;
`;

const MetricsTitle = styled.div`
  font-size: 0.75rem;
  font-weight: 700;
  color: #1976d2;
  margin-bottom: 8px;
  text-align: center;
  text-transform: uppercase;
  letter-spacing: 0.5px;
`;

const MetricRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 6px;
  font-size: 0.8rem;
  
  &:last-child {
    margin-bottom: 0;
  }
`;

const MetricLabel = styled.span`
  color: #666;
  font-weight: 600;
  font-size: 0.8rem;
`;

const MetricValue = styled.span`
  color: #1976d2;
  font-weight: 700;
  font-family: 'Courier New', monospace;
  font-size: 0.8rem;
  letter-spacing: 0.1em;
  position: relative;
  cursor: help;
`;

const MetricTooltip = styled.div`
  position: absolute;
  bottom: calc(100% + 8px);
  left: 50%;
  transform: translateX(-50%);
  padding: 10px 12px;
  background: #333333;
  color: white;
  font-size: 0.7rem;
  border-radius: 6px;
  white-space: normal;
  width: 220px;
  opacity: ${props => props.$show ? 1 : 0};
  pointer-events: ${props => props.$show ? 'auto' : 'none'};
  transition: opacity 0.2s ease;
  z-index: 10000 !important;
  line-height: 1.4;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.3);
  
  &::after {
    content: '';
    position: absolute;
    top: 100%;
    left: 50%;
    transform: translateX(-50%);
    border: 6px solid transparent;
    border-top-color: #333333;
  }
`;

const MetricValueWrapper = styled.span`
  position: relative;
  display: inline-block;
`;

const NavigationContainer = styled.div`
  position: relative;
  left: 50%;
  right: 50%;
  margin-left: -50vw;
  margin-right: -50vw;
  width: 100vw;
  display: flex;
  justify-content: space-between;
  align-items: center;
  background: white;
  border-radius: 0;
  padding: 5px 16px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  box-sizing: border-box;
`;

const NavButton = styled.button`
  background: white;
  border: 1px solid rgba(0, 0, 0, 0.2);
  border-radius: 4px;
  color: #333333;
  padding: 7px 14px;
  font-size: 0.81rem;
  font-weight: 500;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 6px;
  transition: background-color 0.2s ease;

  &:hover:not(:disabled) {
    background: #f5f5f5;
  }

  &:disabled {
    background: #f5f5f5;
    border-color: #e0e0e0;
    color: #999;
    cursor: not-allowed;
    opacity: 0.5;
  }
`;

const PlantNavButton = styled.button`
  background: white;
  border: 2px solid #1976d2;
  border-radius: 6px;
  color: #1976d2;
  padding: 4px 12px;
  font-size: 0.75rem;
  font-weight: 600;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 6px;
  transition: all 0.2s ease;
  white-space: nowrap;

  &:hover:not(:disabled) {
    background: #f5f5f5;
  }

  &:disabled {
    background: #f5f5f5;
    border-color: #e0e0e0;
    color: #999;
    cursor: not-allowed;
    opacity: 0.5;
  }
`;

const ButtonsBar = styled.div`
  position: relative;
  width: 100vw;
  left: 50%;
  margin-left: -50vw;
  background: white;
  border-radius: 0;
  padding: 12px 20px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  box-sizing: border-box;
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-top: 0;
  margin-bottom: 0;
  gap: 14px;
`;

const ToggleContainerWrapper = styled.div`
  position: relative;
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-top: 0;
  margin-bottom: 0;
  gap: 14px;
  width: 100%;
`;


const DatePickerContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  background: transparent;
  border-radius: 0;
  padding: 0;
  border: none;
  flex: 1;
  justify-content: center;
`;

const StyledDatePicker = styled.div`
  .react-datepicker-wrapper {
    width: 100px;
  }

  .react-datepicker__input-container input {
  background: white;
  border: 2px solid #1976d2;
  border-radius: 6px;
    padding: 5px 9px;
    font-size: 0.81rem;
  color: #1976d2;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
    width: 100px;
    text-align: center;

  &:focus {
    outline: none;
    border-color: #facc15;
    box-shadow: 0 0 0 2px rgba(255, 152, 0, 0.2);
  }

  &:hover {
    border-color: #facc15;
    }

    &::placeholder {
      color: #999;
      font-weight: 400;
    }
  }

  .react-datepicker {
    font-family: inherit;
    border: 2px solid #1976d2;
    border-radius: 6px;
  }

  .react-datepicker__header {
    background-color: #1976d2;
    border-bottom: 2px solid #1976d2;
    border-top-left-radius: 4px;
    border-top-right-radius: 4px;
  }

  .react-datepicker__current-month {
    color: white;
    font-weight: 600;
  }

  .react-datepicker__day-name {
    color: white;
    font-weight: 600;
  }

  .react-datepicker__day--selected,
  .react-datepicker__day--keyboard-selected {
    background-color: #1976d2;
    color: white;
  }

  .react-datepicker__day:hover {
    background-color: #facc15;
    color: #333;
  }
`;

const DateLabel = styled.label`
  color: #1976d2;
  font-weight: 600;
  font-size: 0.81rem; /* 10% smaller */
  text-transform: uppercase;
  letter-spacing: 0.5px;
`;

const LastUpdated = styled.div`
  color: white;
  font-size: 1.02rem;
  font-weight: 500;
  padding: 0 15px;
  display: flex;
  align-items: center;
  gap: 5px;
  white-space: nowrap;
`;

const RefreshIconButton = styled.button`
  background: rgba(255, 255, 255, 0.15);
  border: 1px solid rgba(255, 255, 255, 0.25);
  border-radius: 6px;
  color: white;
  padding: 6px;
  font-size: 0.85rem;
  font-weight: 500;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s ease;
  backdrop-filter: blur(10px);
  width: 32px;
  height: 32px;

  &:hover:not(:disabled) {
    background: rgba(255, 255, 255, 0.25);
    transform: translateY(-1px);
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const RefreshSpinner = styled.div`
  width: 50px;
  height: 50px;
  border: 4px solid rgba(255, 255, 255, 0.2);
  border-top: 4px solid white;
  border-radius: 50%;
  animation: spin 1s linear infinite;
  margin-bottom: 20px;

  @keyframes spin {
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
  }
`;

const SegmentedToggle = styled.div`
  display: inline-flex;
  border: 2px solid #1976d2;
  border-radius: 6px; /* more square */
  overflow: hidden;
  height: 22px; /* smaller */
  margin-left: 16px; /* shift to the right */
`;


const SegmentedOption = styled.button`
  background: ${props => props.$active ? '#1976d2' : 'white'};
  color: ${props => props.$active ? 'white' : '#1976d2'};
  border: none;
  padding: 2px 8px; /* smaller, tighter */
  font-size: 0.75rem; /* smaller */
  font-weight: 800;
  cursor: pointer;
  display: flex;
  align-items: center;
`;



const ToggleContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  background: transparent;
  border-radius: 0;
  padding: 12px;
  box-shadow: none;
  transform: scale(0.625); /* 25% bigger */
  transform-origin: center;
  gap: 14px; /* space between buttons */
  flex: 1;
`;

const ToggleButton = styled.button`
  background: ${props => props.$active ? (props.$isReal ? '#ef4444' : '#10b981') : '#f5f5f5'};
  border: 1px solid ${props => props.$active ? (props.$isReal ? '#ef4444' : '#10b981') : '#ddd'};
  border-radius: 4px;
  color: ${props => props.$active ? 'white' : '#666'};
  padding: 10px 21px;
  font-size: 1.4rem;
  font-weight: 500;
  cursor: ${props => props.disabled ? 'not-allowed' : 'pointer'};
  transition: background-color 0.2s ease;
  display: flex;
  align-items: center;
  gap: 8px;
  opacity: ${props => props.disabled ? 0.5 : 1};

  &:hover:not(:disabled) {
    background: ${props => props.$active ? (props.$isReal ? '#dc2626' : '#059669') : '#eaeaea'};
  }

  &:disabled {
    cursor: not-allowed;
  }
`;

const SmallToggleButton = styled.button`
  background: ${props => props.$active ? (props.color || '#e5e7eb') : '#f5f5f5'};
  border: 1px solid ${props => props.$active ? (props.color || '#e5e7eb') : '#ddd'};
  border-radius: 4px;
  color: ${props => props.$active ? 'white' : (props.color ? '#1976d2' : '#333')};
  padding: 12px 20px;
  font-size: 1.4rem;
  font-weight: 600;
  cursor: ${props => props.disabled ? 'not-allowed' : 'pointer'};
  transition: background-color 0.2s ease;
  display: flex;
  align-items: center;
  gap: 8px;
  opacity: ${props => props.disabled ? 0.5 : 1};

  &:hover:not(:disabled) {
    opacity: 0.9;
    background: ${props => props.$active ? (props.color || '#e5e7eb') : '#eaeaea'};
  }

  &:disabled {
    cursor: not-allowed;
  }
`;

const ErrorBox = styled.div`
  background: #ffebee;
  border: 2px solid #f44336;
  color: #c62828;
  padding: 14px;
  border-radius: 10px;
  margin-top: 12px;
`;

const InteractiveLoadingContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 80px 40px;
  background: transparent;
  backdrop-filter: none;
  border-radius: 0;
  box-shadow: none;
  min-height: 400px;
  border: none;
`;

const LoadingSpinner = styled.div`
  width: 48px;
  height: 48px;
  border: 4px solid rgba(255, 255, 255, 0.3);
  border-top: 4px solid white;
  border-radius: 50%;
  animation: spin 1s linear infinite;
  margin-bottom: 24px;
  
  @keyframes spin {
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
  }
`;

const LoadingText = styled.div`
  font-size: 1.25rem;
  font-weight: 600;
  color: white;
  margin-bottom: 8px;
  text-align: center;
  letter-spacing: 0.3px;
`;

const LoadingSteps = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
  margin-top: 24px;
  width: 100%;
  max-width: 420px;
`;

const LoadingStep = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px 18px;
  background: transparent;
  border-radius: 0;
  border: none;
  transition: all 0.3s ease;
  opacity: 1;
`;

const StepIcon = styled.span`
  font-size: 1.1rem;
  width: 24px;
  text-align: center;
  color: white;
`;

const StepText = styled.span`
  font-size: 0.95rem;
  color: white;
  font-weight: 500;
  flex: 1;
`;

const ProgressBar = styled.div`
  width: 100%;
  height: 6px;
  background: rgba(0, 0, 0, 0.06);
  border-radius: 3px;
  overflow: hidden;
  margin-top: 24px;
  max-width: 420px;
`;

const ProgressFill = styled.div`
  height: 100%;
  background: #1976d2;
  border-radius: 3px;
  width: ${props => props.$progress || 0}%;
  transition: width 0.5s ease;
`;

const LoadingProgress = ({ currentStep = 0, totalSteps = 3 }) => {
  return (
    <InteractiveLoadingContainer>
      <LoadingSpinner />
      <LoadingText>Loading Live Data</LoadingText>
    </InteractiveLoadingContainer>
  );
};

const MeteoblueLivePage = ({ plant, onBack, plants = [], onNavigateToPlant, permissions = {} }) => {
  // Helper function to check if a permission is enabled (defaults to true if not set) - memoized
  const hasPermission = useCallback((key) => {
    return permissions[key] !== false;
  }, [permissions]);

  const [rows, setRows] = useState([]);
  const [columns, setColumns] = useState([]);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadingStep, setLoadingStep] = useState(0);
  const [currentDayOffset, setCurrentDayOffset] = useState(0); // 0 = first day, 1 = second day, etc.
  const [startDate, setStartDate] = useState(() => { const d = new Date(); const yyyy = d.getFullYear(); const mm = String(d.getMonth() + 1).padStart(2, '0'); const dd = String(d.getDate()).padStart(2, '0'); return `${yyyy}-${mm}-${dd}`; });
  const [endDate, setEndDate] = useState(() => { const d = new Date(); const yyyy = d.getFullYear(); const mm = String(d.getMonth() + 1).padStart(2, '0'); const dd = String(d.getDate()).padStart(2, '0'); return `${yyyy}-${mm}-${dd}`; }); // Initialize to same as startDate
  // Show only LIVE and Plant by default, but respect permissions
  const [showLiveForecast, setShowLiveForecast] = useState(() => hasPermission('live-intraday-forecast'));
  const [showRealData, setShowRealData] = useState(() => hasPermission('live-production'));
  const [showIrradiance, setShowIrradiance] = useState(false);
  const [showTemperature, setShowTemperature] = useState(false);
  const [showSolcastAggregate, setShowSolcastAggregate] = useState(() => hasPermission('live-day-ahead-forecast'));
  const [timezone, setTimezone] = useState('UTC'); // 'UTC', 'EET', or 'CET'
  const [windowSize, setWindowSize] = useState({ width: window.innerWidth, height: window.innerHeight });
  const [zoomLevel, setZoomLevel] = useState(1);
  
  // Dynamic column detection - will be set after data loads
  const [irradianceKey, setIrradianceKey] = useState(null);
  const [temperatureKey, setTemperatureKey] = useState(null);
  const [hoveredMetric, setHoveredMetric] = useState(null); // 'mae' or 'wape' or null
  const [deltaKey, setDeltaKey] = useState(null);
  const [hoveredBar, setHoveredBar] = useState(null);
  const [hoveredLine, setHoveredLine] = useState(null); // Format: "lineKey-pointIndex"
  const [currentTimeMs, setCurrentTimeMs] = useState(Date.now());
  const [isExporting, setIsExporting] = useState(false);
  const [downtimeRecords, setDowntimeRecords] = useState([]);
  const [maxInverters, setMaxInverters] = useState(0);
  const [lastUpdated, setLastUpdated] = useState(null);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [refreshMessage, setRefreshMessage] = useState('');
  const [refreshElapsed, setRefreshElapsed] = useState(0);

  // Handle window resize and zoom
  useEffect(() => {
    const handleResize = () => {
      setWindowSize({ width: window.innerWidth, height: window.innerHeight });
    };


    // Use a more aggressive zoom detection
    const handleZoomChange = () => {
      // Force immediate update
      setWindowSize({ width: window.innerWidth, height: window.innerHeight });
      setZoomLevel(window.devicePixelRatio || 1);
      
      // Also update after a short delay to catch any delayed changes
      setTimeout(() => {
        setWindowSize({ width: window.innerWidth, height: window.innerHeight });
        setZoomLevel(window.devicePixelRatio || 1);
      }, 100);
    };

    window.addEventListener('resize', handleResize);
    window.addEventListener('wheel', handleZoomChange, { passive: true });
    window.addEventListener('keydown', (e) => {
      if ((e.ctrlKey || e.metaKey) && (e.key === '+' || e.key === '-' || e.key === '0')) {
        handleZoomChange();
      } else if (e.key === '+' || e.key === '=') {
        // Direct + key without modifier
        setZoomLevel(prev => Math.min(prev * 1.1, 3));
      } else if (e.key === '-') {
        // Direct - key without modifier  
        setZoomLevel(prev => Math.max(prev / 1.1, 0.3));
      }
    });

    // Also listen for zoom events
    window.addEventListener('zoom', handleZoomChange);

    return () => {
      window.removeEventListener('resize', handleResize);
      window.removeEventListener('wheel', handleZoomChange);
      window.removeEventListener('keydown', handleZoomChange);
      window.removeEventListener('zoom', handleZoomChange);
    };
  }, []);

  // Update current time periodically so the NOW marker remains accurate
  useEffect(() => {
    const id = setInterval(() => setCurrentTimeMs(Date.now()), 60000); // every 60s
    return () => clearInterval(id);
  }, []);

  // Auto-refresh disabled

  // Update refresh messages and elapsed time when refreshing
  const messageIndexRef = useRef(0);
  
  useEffect(() => {
    if (!isRefreshing) {
      setRefreshMessage('');
      setRefreshElapsed(0);
      messageIndexRef.current = 0;
      return;
    }

    const messages = [
      'Initializing refresh...',
      'Connecting to data source...',
      'Fetching latest forecast data...',
      'Processing weather information...',
      'Updating plant metrics...',
      'Validating data integrity...',
      'Almost done, finalizing updates...'
    ];
    
    // Set initial message
    messageIndexRef.current = 0;
    setRefreshMessage(messages[0]);
    
    // Update message every 3 seconds
    const messageInterval = setInterval(() => {
      messageIndexRef.current = (messageIndexRef.current + 1) % messages.length;
      setRefreshMessage(messages[messageIndexRef.current]);
    }, 3000);
    
    // Update elapsed time every second
    const elapsedInterval = setInterval(() => {
      setRefreshElapsed(prev => prev + 1);
    }, 1000);

    return () => {
      clearInterval(messageInterval);
      clearInterval(elapsedInterval);
    };
  }, [isRefreshing]);

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      setLoadingStep(0);
      setError(null);
      try {
        // Phase 1: Fetch environment details first to get column IDs
        setLoadingStep(1);
        let environmentDetails = null;
        try {
          const envResp = await authenticatedFetch(`/api/plants/${plant.id}/environment/details`);
          const envData = await envResp.json();
          if (envData.success && envData.details) {
            environmentDetails = envData.details;
          }
        } catch (envError) {
          console.warn('Failed to fetch environment details, will use fallback detection:', envError);
        }
        
        // Phase 2: Fetch LIVE and Plant Production together, render when both ready
        const [liveResp, realResp, downtimeResp, maxInvertersResp] = await Promise.all([
          authenticatedFetch(`/api/plants/${plant.id}/meteoblue/live/data`),
          authenticatedFetch(`/api/plants/${plant.id}/meteoblue/real/json`),
          authenticatedFetch(`/api/plants/${plant.id}/inverters/records`),
          authenticatedFetch(`/api/plants/${plant.id}/inverters/max`)
        ]);

        setLoadingStep(2);
        const liveData = await liveResp.json();
        const realData = await realResp.json();
        const downtimeData = await downtimeResp.json();
        const maxInvertersData = await maxInvertersResp.json();
        
        // Store max inverters
        if (maxInvertersData.success && maxInvertersData.maxInverters) {
          setMaxInverters(Number(maxInvertersData.maxInverters) || 0);
        } else {
          setMaxInverters(0);
        }
        
        // Parse and store downtime records
        if (downtimeData.success && downtimeData.records) {
          const records = downtimeData.records.map(record => ({
            start_utc: record.start_utc,
            end_utc: record.end_utc,
            inverters_running: Number(record.inverters_running) || 0,
            startMs: parseTimeUTC(record.start_utc),
            endMs: parseTimeUTC(record.end_utc)
          })).filter(r => Number.isFinite(r.startMs) && Number.isFinite(r.endMs));
          setDowntimeRecords(records);
        } else {
          setDowntimeRecords([]);
        }
        
        if (realData.success && liveData.success) {
          // Start with plant data
          const realRows = realData.data || [];
          const realColumns = realData.headers || [];
          
          // Get LIVE data
          const liveRows = liveData.data || [];
          const liveColumns = liveData.headers || [];
          
          // Merge all columns from both real and live data
          const allColumns = [...new Set([...realColumns, ...liveColumns])];
          
          // Column detection: First try to read from environment file, then fallback to heuristics
          let detectedIrradiance = null;
          let detectedTemperature = null;
          let detectedDelta = null;
          
          // Try to get column IDs from environment file
          if (environmentDetails) {
            // Get column IDs from environment file (remove quotes if present)
            const getColumnId = (value) => {
              if (!value) return null;
              return String(value).replace(/^"|"$/g, '').trim();
            };
            
            const energiaProdottaId = getColumnId(environmentDetails.ENERGIA_PRODOTTA_ID);
            const irraggiamentoModuliId = getColumnId(environmentDetails.IRRAGGIAMENTO_MODULI_ID);
            const temperaturaAmbienteId = getColumnId(environmentDetails.TEMPERATURA_AMBIENTE_ID);
            
            // Check if these columns exist in the real data
            if (energiaProdottaId && realColumns.includes(energiaProdottaId)) {
              detectedDelta = energiaProdottaId;
            }
            if (irraggiamentoModuliId && realColumns.includes(irraggiamentoModuliId)) {
              detectedIrradiance = irraggiamentoModuliId;
            }
            if (temperaturaAmbienteId && realColumns.includes(temperaturaAmbienteId)) {
              detectedTemperature = temperaturaAmbienteId;
            }
          }
          
          // Fallback to heuristic detection if environment file didn't provide all columns
          if (!detectedIrradiance || !detectedTemperature || !detectedDelta) {
            // Detect irradiance and temperature from real data:
            // - Irradiance: typically 0-1500+ W/m² (high values, can be 0 at night)
            // - Temperature: typically -20 to 50°C (lower values, can be negative in winter)
            // Use value ranges to distinguish: irradiance has much higher max values
            const nowTime = Date.now();
            const oneDayMsForDetection = 24 * 60 * 60 * 1000;
            const yesterdayStart = nowTime - (2 * oneDayMsForDetection); // 2 days ago to get yesterday's data
            const yesterdayEnd = nowTime - oneDayMsForDetection; // 1 day ago
            
            // Find numeric columns (exclude timestamp and known columns)
            const excludeColumns = ['timestamp', 'time', 'predicted_energy', 'forecasted_energy'];
            const numericCols = realColumns.filter(col => {
              const lowerCol = col.toLowerCase();
              return !excludeColumns.includes(lowerCol) && 
                     !lowerCol.includes('delta') && 
                     !lowerCol.includes('production');
            });
            
            // Optimized column detection - process rows once
            const yesterdayRows = realRows.filter(row => {
                  const rowTime = parseTimeUTC(row.timestamp);
                  return Number.isFinite(rowTime) && rowTime >= yesterdayStart && rowTime < yesterdayEnd;
                });
              
            // Pre-calculate stats in single pass
            const columnStats = numericCols.map(col => {
              const values = [];
              let hasZeros = false;
              let hasNegatives = false;
              let hasZerosAtNight = false;
              let sum = 0;
              
                for (const row of yesterdayRows) {
                const value = Number(row[col]);
                if (Number.isFinite(value)) {
                  values.push(value);
                  sum += value;
                  if (value === 0) {
                    hasZeros = true;
                    // Check if it's between 00:00 and 02:00 AM UTC
                  const rowTime = parseTimeUTC(row.timestamp);
                  if (Number.isFinite(rowTime)) {
                      const hours = new Date(rowTime).getUTCHours();
                      if (hours >= 0 && hours < 2) {
                      hasZerosAtNight = true;
                      }
                    }
                  }
                  if (value < 0) hasNegatives = true;
                }
              }
              
              if (values.length === 0) return null;
              
              const min = Math.min(...values);
              const max = Math.max(...values);
              const avg = sum / values.length;
              
              return {
                col,
                min,
                max,
                avg,
                hasZeros,
                hasZerosAtNight,
                hasNegatives,
                count: values.length
              };
            }).filter(stat => stat !== null && stat.count > 0);
            
            // Irradiance: high max values (typically > 100) AND has zeros between 00:00 and 02:00 AM UTC
            if (!detectedIrradiance) {
              const irradianceCol = columnStats.find(stat => stat.max > 100 && stat.hasZerosAtNight);
              if (irradianceCol) {
                detectedIrradiance = irradianceCol.col;
              }
            }
            
            // Temperature: the remaining column after irradiance is identified
            if (!detectedTemperature) {
              let temperatureCol = null;
              if (detectedIrradiance) {
                // If irradiance is found, temperature is the other column
                temperatureCol = columnStats.find(stat => stat.col !== detectedIrradiance);
              } else {
                // Fallback: try to find temperature by other means if irradiance not found
                temperatureCol = columnStats.find(stat => 
                  stat.max < 100 && 
                  (stat.hasNegatives || stat.min < 10)
                );
              }
              if (temperatureCol) {
                detectedTemperature = temperatureCol.col;
              }
            }
            
            // Detect delta/production column
            if (!detectedDelta) {
              const deltaCol = realColumns.find(col => {
                const lowerCol = col.toLowerCase();
                return lowerCol.includes('delta') || lowerCol.includes('production');
              });
              if (deltaCol) {
                detectedDelta = deltaCol;
              }
            }
          }
          
          // Set keys early so they're available during processing
          setIrradianceKey(detectedIrradiance);
          setTemperatureKey(detectedTemperature);
          setDeltaKey(detectedDelta);
          
          // Use detected keys for processing
          const irradianceKeyLocal = detectedIrradiance;
          const temperatureKeyLocal = detectedTemperature;
          const deltaKeyLocal = detectedDelta;
          
          // Create a map of LIVE data by normalized timestamp for quick lookup
          const liveDataMap = new Map();
          liveRows.forEach(row => {
            if (row.timestamp) {
              const normalizedTime = parseTimeUTC(row.timestamp);
              if (Number.isFinite(normalizedTime)) {
                liveDataMap.set(normalizedTime, row);
              }
            }
          });
          
          // Merge LIVE data with plant data (no forecast yet - that loads in background)
          const mergedRows = realRows.map(plantRow => {
            const plantTimestamp = parseTimeUTC(plantRow.timestamp);
            const liveRow = Number.isFinite(plantTimestamp) ? liveDataMap.get(plantTimestamp) : null;
            
            const mergedRow = { ...plantRow };
            
            if (liveRow && liveRow.predicted_energy) {
              mergedRow.predicted_energy = liveRow.predicted_energy;
            }
            
            return mergedRow;
          });
          
          // Add LIVE data that doesn't have plant data (ALL data, no time limit)
          const existingTimestamps = new Set(
            mergedRows.map(r => parseTimeUTC(r.timestamp)).filter(t => Number.isFinite(t))
          );
          
          liveRows.forEach(liveRow => {
            if (liveRow.timestamp) {
              const liveTimestamp = parseTimeUTC(liveRow.timestamp);
              if (Number.isFinite(liveTimestamp) && !existingTimestamps.has(liveTimestamp)) {
                if (liveRow.predicted_energy != null) {
                  const newRow = {
                    timestamp: liveRow.timestamp,
                    [deltaKeyLocal || 'D.156.DELTA']: '',
                    [irradianceKeyLocal || '']: '',
                    [temperatureKeyLocal || '']: '',
                    predicted_energy: liveRow.predicted_energy
                  };

                  mergedRows.push(newRow);
                }
              }
            }
          });
          
          mergedRows.sort((a, b) => parseTimeUTC(a.timestamp) - parseTimeUTC(b.timestamp));

          setLoadingStep(3);
          
          // Fetch forecast and last updated in parallel before displaying
          const [forecastResp, lastUpdatedResp] = await Promise.all([
            authenticatedFetch(`/api/plants/${plant.id}/15min/forecast/aggregate`).catch(() => ({ json: async () => ({ success: false, data: [] }) })),
            authenticatedFetch(`/api/plants/${plant.id}/last-updated`).catch(() => ({ ok: false }))
          ]);
          
          // Process forecast data
              const forecastData = await forecastResp.json();
              const forecastRows = forecastData.success ? (forecastData.data || []) : [];
          
          let finalRows = mergedRows;
          let finalColumns = allColumns;
              
              if (forecastRows.length > 0) {
                const forecastDataMap = new Map();
                forecastRows.forEach(row => {
                  if (row.timestamp) {
                    const normalizedTime = parseTimeUTC(row.timestamp);
                    if (Number.isFinite(normalizedTime)) {
                      forecastDataMap.set(normalizedTime, row);
                    }
                  }
                });
                
                // Merge forecast into existing rows
            finalRows = mergedRows.map(row => {
                  const t = parseTimeUTC(row.timestamp);
                  const forecastRow = Number.isFinite(t) ? forecastDataMap.get(t) : null;
                  if (forecastRow && forecastRow.forecasted_energy) {
                    return { ...row, forecasted_energy: forecastRow.forecasted_energy };
                  }
                  return row;
                });
                
            if (!finalColumns.includes('forecasted_energy')) {
              finalColumns = [...allColumns, 'forecasted_energy'];
            }
          }
          
          // Process last updated timestamp
          try {
            if (lastUpdatedResp.ok) {
              const lastUpdatedData = await lastUpdatedResp.json();
              if (lastUpdatedData.success && lastUpdatedData.utcYear && lastUpdatedData.utcMonth !== undefined && lastUpdatedData.utcDay !== undefined) {
                const utcData = {
                  utcYear: lastUpdatedData.utcYear,
                  utcMonth: lastUpdatedData.utcMonth,
                  utcDay: lastUpdatedData.utcDay,
                  utcHour: lastUpdatedData.utcHour !== undefined ? lastUpdatedData.utcHour : 0,
                  utcMinute: lastUpdatedData.utcMinute !== undefined ? lastUpdatedData.utcMinute : 0,
                  utcSecond: lastUpdatedData.utcSecond !== undefined ? lastUpdatedData.utcSecond : 0
                };
                setLastUpdated(utcData);
              }
              }
            } catch (err) {
            // Silently fail - don't show timestamp if we can't fetch it
            }
          
          // Forward-fill temperature data to reach the now line, then stop
          const currentTime = Date.now();
          
          // Find the last valid temperature value
          let lastValidTemp = null;
          for (let i = finalRows.length - 1; i >= 0; i--) {
            const row = finalRows[i];
            const rowTime = parseTimeUTC(row.timestamp);
            if (Number.isFinite(rowTime) && rowTime <= currentTime) {
              if (temperatureKeyLocal && row[temperatureKeyLocal] && !isNaN(Number(row[temperatureKeyLocal]))) {
                lastValidTemp = Number(row[temperatureKeyLocal]);
                break;
              }
            }
          }
          
          // Process temperature forward-fill if we have valid data
          if (lastValidTemp !== null) {
            finalRows = finalRows.map(row => {
            const rowTime = parseTimeUTC(row.timestamp);
            const filteredRow = { ...row };
            
              // Forward-fill temperature data to reach the now line
              if (Number.isFinite(rowTime) && rowTime <= currentTime) {
                // If this row has no temperature data but is before now, forward-fill it
                if (temperatureKeyLocal && (!row[temperatureKeyLocal] || isNaN(Number(row[temperatureKeyLocal])))) {
                  filteredRow[temperatureKeyLocal] = lastValidTemp;
                }
              } else if (Number.isFinite(rowTime) && rowTime > currentTime) {
                // Remove temperature data completely for future dates beyond now (line becomes invisible)
                if (temperatureKeyLocal) {
                  delete filteredRow[temperatureKeyLocal];
              }
            }
            
            return filteredRow;
          });
          }
          
          // Set all data at once - only one render
          setRows(finalRows);
          setColumns(finalColumns);
          setLoading(false);
          
        } else {
          // Data fetch failed for plant
          setError(realData.error || liveData.error || 'Failed to load Meteoblue data');
          setLoading(false);
        }
      } catch (e) {
        // Error fetching data for plant
        setError('Failed to load data');
        setLoading(false);
      }
    };
    fetchData();
  }, [plant]);

  const numericColumns = useMemo(() => {
    return columns.filter(c => c.toLowerCase() !== 'time' && c.toLowerCase() !== 'timestamp');
  }, [columns]);

  const timeAccessor = useMemo(() => {
    const lc = columns.map(c => c.toLowerCase());
    const idx = lc.indexOf('time') !== -1 ? lc.indexOf('time') : lc.indexOf('timestamp');
    return idx !== -1 ? columns[idx] : null;
  }, [columns]);

  // After data loads and timeAccessor is available, default the view to the current UTC day
  useEffect(() => {
    if (!timeAccessor || rows.length === 0) return;
    if (startDate || endDate) return; // don't override explicit date selection

    const oneDayMs = 24 * 60 * 60 * 1000;
    const times = rows.map(r => parseTimeUTC(r[timeAccessor])).filter(t => Number.isFinite(t));
    if (times.length === 0) return;
    const firstTime = Math.min(...times);
    const now = Date.now();
    const idx = Math.max(0, Math.floor((now - firstTime) / oneDayMs));

    // Calculate total available days (including future days)
    const lastTime = Math.max(...times);
    const totalDaysAvail = Math.max(1, Math.ceil(((lastTime - firstTime) + 1) / oneDayMs));
    
    // Default to current day, but allow navigation to all available days
    const clamped = Math.min(idx, totalDaysAvail - 1);
    setCurrentDayOffset(clamped);
  }, [timeAccessor, rows, startDate, endDate]);

  const width = window.innerWidth; // Full viewport width
  const height = Math.max(500, (windowSize.height - 250) * 0.95); // Taller plot
  const padding = { left: 70, right: 20, top: 20, bottom: 50 }; // Left padding for y-axis labels, right 20px from edge

  const getDateRangeData = useCallback(() => {
    if (rows.length === 0) return rows;
    // Determine a usable time key
    let key = timeAccessor;
    if (!key) {
      const sample = rows.find(r => r && typeof r === 'object');
      if (sample) {
        const cand = Object.keys(sample).find(k => /(time|timestamp|date|datetime|period)/i.test(k) && Number.isFinite(parseTimeUTC(sample[k])));
        if (cand) key = cand;
      }
    }
    if (!key) return rows;

    if (!startDate && !endDate) return rows;

    const parseT = (v) => parseTimeUTC(v);
    let filteredRows = rows;

    if (startDate) {
      const startTime = new Date(startDate).getTime();
      if (!endDate) {
        const oneDayMs = 24 * 60 * 60 * 1000;
        const singleDayEnd = startTime + oneDayMs;
        filteredRows = filteredRows.filter(r => {
          const t = parseT(r[key]);
          return t >= startTime && t < singleDayEnd;
        });
      } else {
        filteredRows = filteredRows.filter(r => parseT(r[key]) >= startTime);
      }
    }

    if (endDate) {
      const endTime = new Date(endDate).getTime() + (24 * 60 * 60 * 1000); // inclusive end of day
      filteredRows = filteredRows.filter(r => parseT(r[key]) < endTime);
    }

    // If multiple days are selected, fill missing forecast values with 0
    const isMultipleDays = startDate && endDate && startDate !== endDate;
    if (isMultipleDays && columns.includes('forecasted_energy')) {
      filteredRows = filteredRows.map(row => {
        if (row.forecasted_energy == null || !Number.isFinite(Number(row.forecasted_energy))) {
          return { ...row, forecasted_energy: 0 };
        }
        return row;
      });
    }

    return filteredRows;
  }, [rows, timeAccessor, startDate, endDate, columns]);

  // Calculate scale separately and store it - only recalculate when data actually changes
  const plotScale = useMemo(() => {
    if (!timeAccessor || rows.length === 0) return { globalVMin: 0, globalVMax: 0.1 };
    
    const filteredRows = getDateRangeData();
    if (filteredRows.length === 0) return { globalVMin: 0, globalVMax: 0.1 };
    
    const times = filteredRows.map(r => parseTimeUTC(r[timeAccessor]));
    if (times.length === 0) return { globalVMin: 0, globalVMax: 0.1 };
    
    // Determine data window
    let dataToPlot;
    let dayGroups = null;
    
    if (startDate || endDate) {
      dataToPlot = filteredRows;
    } else {
      dayGroups = [];
      const oneDayMs = 24 * 60 * 60 * 1000;
      const dayMap = new Map();
      
      filteredRows.forEach((row, idx) => {
        const time = times[idx];
        if (Number.isFinite(time)) {
          const dayKey = Math.floor(time / oneDayMs);
          if (!dayMap.has(dayKey)) {
            dayMap.set(dayKey, []);
          }
          dayMap.get(dayKey).push(row);
        }
      });
      
      const sortedDays = Array.from(dayMap.keys()).sort();
      sortedDays.forEach((dayKey, dayIndex) => {
        const dayStart = dayKey * oneDayMs;
        const dayEnd = dayStart + oneDayMs;
        const dayData = dayMap.get(dayKey);
        
        dayGroups.push({
          index: dayIndex,
          startTime: dayStart,
          endTime: dayEnd,
          data: dayData
        });
      });
      
      const currentDay = dayGroups[currentDayOffset];
      if (!currentDay) return { globalVMin: 0, globalVMax: 0.1 };
      
      dataToPlot = currentDay.data;
    }
    
    if (dataToPlot.length === 0) return { globalVMin: 0, globalVMax: 0.1 };
    
    // Get all possible values from ALL data sources (regardless of toggles) for stable scaling
    const forecastKey = ['predicted_energy', 'expected_production', 'forecast', 'Forecast', 'prod_forecast']
      .find(k => numericColumns.includes(k));
    const realKey = [deltaKey || 'D.156.DELTA', 'production', 'real_production']
      .find(k => numericColumns.includes(k));
    
    const dayValuesRaw = [];
    // Always include all available data for scaling, regardless of toggles
    if (forecastKey) {
      dayValuesRaw.push(...dataToPlot.map(r => Number(r[forecastKey])).filter(v => Number.isFinite(v)));
    }
    if (realKey) {
      dayValuesRaw.push(...dataToPlot.map(r => Number(r[realKey])).filter(v => Number.isFinite(v)));
    }
    // Always include day-ahead forecast if permission allows
    if (hasPermission('live-day-ahead-forecast') && numericColumns.includes('forecasted_energy')) {
      dayValuesRaw.push(...dataToPlot.map(r => Number(r['forecasted_energy'])).filter(v => Number.isFinite(v)));
    }
    
    const dayValues = dayValuesRaw.filter(v => Number.isFinite(v));
    const dayMax = dayValues.length ? Math.max(...dayValues) : 0.1;
    const plotMaxValue = Math.ceil(dayMax * 10) / 10;
    
    return {
      globalVMin: 0,
      globalVMax: plotMaxValue
    };
  }, [rows, timeAccessor, startDate, endDate, currentDayOffset, numericColumns, deltaKey, hasPermission, getDateRangeData]);

  const seriesData = useMemo(() => {
    if (!timeAccessor || rows.length === 0) return [];
    
    // Get date-filtered data
    const filteredRows = getDateRangeData();
    if (filteredRows.length === 0) return [];
    
    const times = filteredRows.map(r => parseTimeUTC(r[timeAccessor]));
    
    if (times.length === 0) return [];
    
    // If date range is selected, show all data in the range
    // If no date range, use day navigation
    let dataToPlot;
    let timesToPlot;
    let dayGroups = null;
    
    if (startDate || endDate) {
      // Show all data in the selected date range
      dataToPlot = filteredRows;
      timesToPlot = times;
    } else {
      // Use day navigation for unfiltered data
      dayGroups = [];
      const oneDayMs = 24 * 60 * 60 * 1000;
      
      // Group data by day
      const dayMap = new Map();
      
      filteredRows.forEach((row, idx) => {
        const time = times[idx];
        if (Number.isFinite(time)) {
          const dayKey = Math.floor(time / oneDayMs);
          if (!dayMap.has(dayKey)) {
            dayMap.set(dayKey, []);
          }
          dayMap.get(dayKey).push(row);
        }
      });
      
      // Convert to array and sort by day
      const sortedDays = Array.from(dayMap.keys()).sort();
      sortedDays.forEach((dayKey, dayIndex) => {
        const dayStart = dayKey * oneDayMs;
        const dayEnd = dayStart + oneDayMs;
        const dayData = dayMap.get(dayKey);
        
        dayGroups.push({
          index: dayIndex,
          startTime: dayStart,
          endTime: dayEnd,
          data: dayData
        });
      });
      
      // Get the current day based on offset
      const currentDay = dayGroups[currentDayOffset];
      
      if (!currentDay) return [];
      
      dataToPlot = currentDay.data;
      timesToPlot = dataToPlot.map(r => parseTimeUTC(r[timeAccessor]));
    }
    
    const oneDayData = dataToPlot;
    const oneDayTimes = timesToPlot;
    
    if (oneDayData.length === 0) return [];
    
    // We only want two series side-by-side per timestamp: forecast vs real production
    // Identify canonical keys for Meteoblue
    const forecastKey = ['predicted_energy', 'expected_production', 'forecast', 'Forecast', 'prod_forecast']
      .find(k => numericColumns.includes(k));
    const realKey = [deltaKey || 'D.156.DELTA', 'production', 'real_production']
      .find(k => numericColumns.includes(k));

    // Build columns list based on toggles, permissions, and availability, with sensible fallbacks
    let columnsToShow = [
      ...(showLiveForecast && hasPermission('live-intraday-forecast') && forecastKey ? [forecastKey] : []),
      ...(showRealData && hasPermission('live-production') && realKey ? [realKey] : [])
    ];
    if (columnsToShow.length === 0) {
      // Fallback to any available numeric column to ensure something renders
      if (forecastKey) columnsToShow = [forecastKey];
      else if (realKey) columnsToShow = [realKey];
      else if (numericColumns.length > 0) columnsToShow = [numericColumns[0]];
    }
    
    // Always use the full day data to center the plot
    let filteredOneDayData = oneDayData;
    let filteredOneDayTimes = oneDayTimes;
    
    if (filteredOneDayData.length === 0) return [];
    
    // Always show 00:00 to 24:00 for the selected day in the selected timezone
    let dayStartMs, dayEndMs;
    
    if (startDate || endDate) {
      // For date range, use the first day's 00:00 to last day's 24:00
      const firstTime = Math.min(...timesToPlot);
      const lastTime = Math.max(...timesToPlot);
      
      // Get the day boundaries in UTC
      const firstDay = new Date(firstTime);
      firstDay.setUTCHours(0, 0, 0, 0);
      const lastDay = new Date(lastTime);
      lastDay.setUTCHours(24, 0, 0, 0);
      
      dayStartMs = firstDay.getTime();
      dayEndMs = lastDay.getTime();
    } else {
      // For day navigation, use the current day's boundaries
      const currentDay = dayGroups[currentDayOffset];
      if (!currentDay) return [];
      
      // Get day boundaries in UTC
      dayStartMs = currentDay.startTime;
      dayEndMs = currentDay.endTime;
    }
    
    // Always use 00:00 to 24:00 regardless of data range
    const tMin = dayStartMs;
    const tMax = dayEndMs;
    const xScale = (t) => padding.left + ((t - tMin) / Math.max(1, (tMax - tMin))) * (width - padding.left - padding.right);
    
    // Use the pre-calculated stable scale
    const globalVMin = plotScale.globalVMin;
    const globalVMax = plotScale.globalVMax;
      
      // Calculate number of days in the selected range
      const oneDayMs = 24 * 60 * 60 * 1000;
      const numberOfDays = Math.max(1, Math.ceil((tMax - tMin) / oneDayMs));
      
      // Keep space between bars - don't overlap completely
      // Use fixed number of intervals per day (96 for 15-minute intervals) for consistent bar width
      const expectedIntervalsPerDay = 96; // 24 hours * 4 intervals per hour (15-minute intervals)
      // Use wider bars for single day (0.7), narrower for multiple days (0.4)
      const baseWidthFactor = numberOfDays === 1 ? 0.7 : 0.4;
      const baseBarWidth = (width - padding.left - padding.right) / expectedIntervalsPerDay * baseWidthFactor;
      
      // Scale down in groups with progressively more aggressive scaling
      let scaleFactor = 1.0; // Default: 100% for 1 day
      if (numberOfDays >= 2 && numberOfDays <= 5) {
        scaleFactor = 0.55; // 45% reduction for 2-5 days (55% width)
      } else if (numberOfDays >= 6 && numberOfDays <= 10) {
        scaleFactor = 0.5; // 50% reduction for 6-10 days (50% width)
      } else if (numberOfDays >= 11 && numberOfDays <= 20) {
        scaleFactor = 0.25; // 75% reduction for 11-20 days (25% width)
      } else if (numberOfDays >= 21 && numberOfDays <= 30) {
        scaleFactor = 0.15; // 85% reduction for 21-30 days (15% width)
      } else if (numberOfDays > 30) {
        scaleFactor = 0.1; // 90% reduction for 30+ days (10% width - minimum)
      }
      
      const groupBarWidth = Math.max(2, baseBarWidth * scaleFactor);
      const singleBarWidth = groupBarWidth; // same width for both series

      // Ensure forecast series renders first, real second (on top)
      const orderedColumns = columnsToShow.slice().sort((a, b) => {
        const isAReal = a === realKey;
        const isBReal = b === realKey;
        if (isAReal === isBReal) return 0;
        return isAReal ? 1 : -1; // real after forecast
      });

      const result = orderedColumns.map((col, seriesIdx) => {
        // Calculate yScale: map values from globalVMin to globalVMax to full plot height
        // Use Math.max with a small epsilon to avoid division by zero, but allow values < 1
        const range = globalVMax - globalVMin;
        const yScale = (v) => height - padding.bottom - ((v - globalVMin) / Math.max(0.01, range)) * (height - padding.top - padding.bottom);
      
        // Create bars at identical x so they overlap; rendering order controls which is in front
        const bars = filteredOneDayData.map((r, idx) => {
          const x = xScale(filteredOneDayTimes[idx]);
          const y = yScale(Number(r[col]));
          const barWidth = singleBarWidth;
          return {
            x: x - barWidth / 2,
            y: y,
            width: barWidth,
            height: height - padding.bottom - y,
            value: Number(r[col])
          };
        });
      
        // Color by series: real = red, forecast = green (slightly stronger)
        const isRealData = col === realKey;
        const color = isRealData ? 'rgba(239, 68, 68, 0.7)' : 'rgba(16, 185, 129, 0.7)';
        
        return { 
          key: col, 
          color: color,
          isReal: isRealData,
          bars, 
          xScale, 
          yScale, 
          vMin: globalVMin, 
          vMax: globalVMax, 
          tMin, 
          tMax,
          timestamps: filteredOneDayData.map(r => r[timeAccessor])
        };
    });
    return result;
  }, [rows, numericColumns, timeAccessor, width, currentDayOffset, startDate, endDate, showLiveForecast, showRealData, zoomLevel, deltaKey, permissions, plotScale, getDateRangeData]);

  // Line series for irradiance (171) and temperature (172) from Plant Data CSV
  // Scaled into the SAME y-range and time window as the bars so they "match the plot"
  const lineSeriesData = useMemo(() => {
    if (!timeAccessor || rows.length === 0) return [];
    if (seriesData.length === 0) return [];

    const filteredRows = getDateRangeData();
    if (filteredRows.length === 0) return [];

    const times = filteredRows.map(r => parseTimeUTC(r[timeAccessor]));
    if (times.length === 0) return [];

    // Use the same time window and scales as the first bar series
    const ref = seriesData.find(s => s.isReal) || seriesData[0];
    const refX = ref.xScale;
    const refY = ref.yScale;

    // Determine data window matching bars logic
    let dataToPlot;
    let timesToPlot;
    if (startDate || endDate) {
      dataToPlot = filteredRows;
      timesToPlot = times;
    } else {

      const dayGroups = [];
      const oneDayMs = 24 * 60 * 60 * 1000;
      let currentDayStart = Math.min(...times);
      let dayIndex = 0;
      while (currentDayStart < Math.max(...times)) {
        const dayEnd = currentDayStart + oneDayMs;
        const dayData = filteredRows.filter((r, idx) => {
          const t = times[idx];
          return t >= currentDayStart && t < dayEnd;
        });
        if (dayData.length > 0) {
          dayGroups.push({ index: dayIndex, startTime: currentDayStart, endTime: dayEnd, data: dayData });
          dayIndex++;
        }
        currentDayStart = dayEnd;
      }
      const currentDay = dayGroups[currentDayOffset];
      if (!currentDay) return [];
      dataToPlot = currentDay.data;
      timesToPlot = dataToPlot.map(r => parseTimeUTC(r[timeAccessor]));
    }

    // Calculate a fixed reference scale based on enabled bar data (forecast + production)
    // only include data from features that have permission
    const forecastKey = ['predicted_energy', 'expected_production', 'forecast', 'Forecast', 'prod_forecast']
      .find(k => numericColumns.includes(k));
    const realKey = [deltaKey || 'D.156.DELTA', 'production', 'real_production']
      .find(k => numericColumns.includes(k));
    
    // Get values only from enabled permissions
    const allBarValues = [];
    if (forecastKey && hasPermission('live-intraday-forecast')) {
      allBarValues.push(...dataToPlot.map(r => Number(r[forecastKey])).filter(v => Number.isFinite(v)));
    }
    if (realKey && hasPermission('live-production')) {
      allBarValues.push(...dataToPlot.map(r => Number(r[realKey])).filter(v => Number.isFinite(v)));
    }
    // Include day-ahead forecast in scaling if permission allows
    if (hasPermission('live-day-ahead-forecast') && numericColumns.includes('forecasted_energy')) {
      allBarValues.push(...dataToPlot.map(r => Number(r['forecasted_energy'])).filter(v => Number.isFinite(v)));
    }
    
    const fixedVMin = allBarValues.length ? Math.min(...allBarValues) : 0;
    const fixedVMax = allBarValues.length ? Math.max(...allBarValues) : 1;
    const refVMin = Math.min(0, fixedVMin); // force baseline at 0
    const refVMax = fixedVMax === fixedVMin ? fixedVMin + 0.1 : fixedVMax;

    const mapToBandInBarRangeZeroAnchored = (value, selfMax, bandStart, bandEnd) => {
      if (!Number.isFinite(value) || !Number.isFinite(selfMax) || selfMax === 0) {
        return refVMin;
      }
      const n = value / selfMax; // 0..selfMax mapped to 0..1
      const minY = refVMin + bandStart * (refVMax - refVMin);
      const maxY = refVMin + bandEnd * (refVMax - refVMin);
      return minY + n * (maxY - minY);
    };

    const mapToBandInBarRangeSelfRange = (value, selfMin, selfMax, bandStart, bandEnd) => {
      if (!Number.isFinite(value) || !Number.isFinite(selfMin) || !Number.isFinite(selfMax) || selfMax - selfMin === 0) {
        return refVMin;
      }
      const n = (value - selfMin) / (selfMax - selfMin); // original behavior
      const minY = refVMin + bandStart * (refVMax - refVMin);
      const maxY = refVMin + bandEnd * (refVMax - refVMin);
      return minY + n * (maxY - minY);
    };

    const lineTimesToPlot = timesToPlot;
    
    const makeLine = (col, color, band = [0, 1], mode = 'self') => {
      const rawValues = filteredRows.map(r => Number(r[col]));
      // Filter out NaN values, but keep zeros for now
      const values = rawValues.filter(v => !Number.isNaN(v));
      if (values.length === 0) return null;
      const selfMin = Math.min(...values);
      const selfMax = Math.max(...values);
      
      // For temperature line, filter out future data beyond now
      const currentTime = Date.now();
      const points = filteredRows.map((r, idx) => {
        const v = Number(r[col]);
        const time = lineTimesToPlot[idx];
        
        // For temperature and irradiance lines, skip future data beyond now
        if ((col === temperatureKey || col === irradianceKey) && Number.isFinite(time) && time > currentTime) {
          return null;
        }
        
        // Don't skip any values - plot everything else
        const mapped = mode === 'zero'
          ? mapToBandInBarRangeZeroAnchored(v, selfMax, band[0], band[1])
          : mapToBandInBarRangeSelfRange(v, selfMin, selfMax, band[0], band[1]);
        return { x: refX(lineTimesToPlot[idx]), y: refY(mapped) };
      }).filter(p => p !== null);
      return { key: col, color, points };
    };

    const result = [];
    
    // Add irradiance line (171) - only if permission allows
    if (hasPermission('live-irradiance') && numericColumns.includes(irradianceKey)) {
      const irr = makeLine(irradianceKey, '#f59e0b', [0, 1], 'self');
      if (irr && irr.points.length > 0) result.push(irr);
    }
    
    // Add temperature line (172) - only if permission allows
    if (hasPermission('live-temperature') && numericColumns.includes(temperatureKey)) {
      const tmp = makeLine(temperatureKey, '#3b82f6', [0.2, 0.8], 'self');
      if (tmp && tmp.points.length > 0) result.push(tmp);
    }
    
    // Add Solcast aggregate if available - use same scale as bars - only if permission allows AND toggle is on
    if (hasPermission('live-day-ahead-forecast') && showSolcastAggregate && numericColumns.includes('forecasted_energy')) {
      // Use the same data window and y-scale as bars
      const points = dataToPlot.map((r, idx) => {
        const v = Number(r['forecasted_energy']);
        const time = timesToPlot[idx];
        if (!Number.isFinite(v) || !Number.isFinite(time)) return null;
        // Use the same yScale as bars - this ensures same scale
        return { x: refX(time), y: refY(v) };
      }).filter(p => p !== null);
      if (points.length > 0) {
        result.push({ key: 'forecasted_energy', color: '#6b7280', points });
      }
    }
    
    return result;
  }, [rows, numericColumns, timeAccessor, seriesData, currentDayOffset, startDate, endDate, irradianceKey, temperatureKey, permissions, showSolcastAggregate, getDateRangeData]);

  // Calculate current inverters down based on active downtime records
  const currentInvertersDown = useMemo(() => {
    if (!maxInverters || !downtimeRecords.length) return 0;
    
    const now = currentTimeMs;
    // Find active downtime records (current time is between start and end)
    const activeRecords = downtimeRecords.filter(record => 
      record.startMs <= now && now <= record.endMs
    );
    
    if (activeRecords.length === 0) return 0;
    
    // Get the most recent active record (in case of overlapping periods)
    const latestRecord = activeRecords.reduce((latest, record) => 
      record.startMs > latest.startMs ? record : latest
    );
    
    // Calculate inverters down: total - running
    const invertersRunning = latestRecord.inverters_running || 0;
    const invertersDown = Math.max(0, maxInverters - invertersRunning);
    
    return invertersDown;
  }, [maxInverters, downtimeRecords, currentTimeMs]);

  // Calculate total days available
  const totalDays = useMemo(() => {
    if (!timeAccessor || rows.length === 0) return 0;
    const filteredRows = getDateRangeData();
    if (filteredRows.length === 0) return 0;
    const times = filteredRows.map(r => parseTimeUTC(r[timeAccessor]));
    if (times.length === 0) return 0;
    const oneDayMs = 24 * 60 * 60 * 1000;
    const firstTime = Math.min(...times);
    const lastTime = Math.max(...times);
    // Include partial last day as a full navigable day
    const totalRangeMs = (lastTime - firstTime) + 1;
    const totalDays = Math.max(1, Math.ceil(totalRangeMs / oneDayMs));
    
    
    return totalDays;
  }, [rows, timeAccessor, startDate, endDate]);

  const handlePreviousDay = () => {
    // If a date range is selected, jump to the previous single day (day before startDate)
    if (startDate || endDate) {
      const baseDateStr = startDate || endDate;
      if (baseDateStr) {
        const base = new Date(baseDateStr);
        base.setDate(base.getDate() - 1);
        const yyyy = base.getFullYear();
        const mm = String(base.getMonth() + 1).padStart(2, '0');
        const dd = String(base.getDate()).padStart(2, '0');
        const prevStr = `${yyyy}-${mm}-${dd}`; // Keep internal format as YYYY-MM-DD
        // Only set both to same day if viewing a single day (startDate === endDate)
        if (startDate && endDate && startDate === endDate) {
          setStartDate(prevStr);
          setEndDate(prevStr);
        } else {
          // If viewing a range, just update start date
          setStartDate(prevStr);
        }
        return;
      }
    }
    // Default: navigate by offset
    if (currentDayOffset > 0) {
      setCurrentDayOffset(currentDayOffset - 1);
    }
  };

  const handleNextDay = () => {
    // If a date range is selected, jump to the next single day (day after endDate, or after startDate if end empty)
    if (startDate || endDate) {
      const baseDateStr = endDate || startDate;
      if (baseDateStr) {
        const base = new Date(baseDateStr);
        base.setDate(base.getDate() + 1);
        const yyyy = base.getFullYear();
        const mm = String(base.getMonth() + 1).padStart(2, '0');
        const dd = String(base.getDate()).padStart(2, '0');
        const nextStr = `${yyyy}-${mm}-${dd}`; // Keep internal format as YYYY-MM-DD
        // Only set both to same day if viewing a single day (startDate === endDate)
        if (startDate && endDate && startDate === endDate) {
          setStartDate(nextStr);
          setEndDate(nextStr);
        } else {
          // If viewing a range, just update end date
          setEndDate(nextStr);
        }
        return;
      }
    }
    // Default: navigate by offset
    if (currentDayOffset < totalDays - 1) {
      setCurrentDayOffset(currentDayOffset + 1);
    }
  };

  // Get current day in YYYY-MM-DD format for date input
  const getCurrentDayISO = () => {
    if (!timeAccessor || rows.length === 0) return '';
    const times = rows.map(r => parseTimeUTC(r[timeAccessor]));
    if (times.length === 0) return '';
    
    const oneDayMs = 24 * 60 * 60 * 1000;
    const firstTime = Math.min(...times);
    const currentDayStart = new Date(firstTime + (currentDayOffset * oneDayMs));
    const yyyy = currentDayStart.getFullYear();
    const mm = String(currentDayStart.getMonth() + 1).padStart(2, '0');
    const dd = String(currentDayStart.getDate()).padStart(2, '0');
    return `${yyyy}-${mm}-${dd}`;
  };

  // Ensure Start Date and End Date are set to current day on first load
  useEffect(() => {
    if (!timeAccessor || rows.length === 0) return;
    if (startDate || endDate) return; // don't override explicit user selection
    const iso = getCurrentDayISO();
    if (iso) {
      setStartDate(iso);
      setEndDate(iso); // Set endDate equal to startDate on first load
    }
  }, [timeAccessor, rows]);

  // Force state to false when permissions are disabled
  useEffect(() => {
    if (!hasPermission('live-day-ahead-forecast')) {
      setShowSolcastAggregate(false);
    }
    if (!hasPermission('live-intraday-forecast')) {
      setShowLiveForecast(false);
    }
    if (!hasPermission('live-production')) {
      setShowRealData(false);
    }
    if (!hasPermission('live-irradiance')) {
      setShowIrradiance(false);
    }
    if (!hasPermission('live-temperature')) {
      setShowTemperature(false);
    }
  }, [permissions]);



  // Convert YYYY-MM-DD string to Date object
  const isoToDate = (isoString) => {
    if (!isoString) return null;
    const parts = isoString.split('-');
    if (parts.length !== 3) return null;
    return new Date(parseInt(parts[0]), parseInt(parts[1]) - 1, parseInt(parts[2]));
  };

  // Convert Date object to YYYY-MM-DD string
  const dateToIso = (date) => {
    if (!date) return '';
    const yyyy = date.getFullYear();
    const mm = String(date.getMonth() + 1).padStart(2, '0');
    const dd = String(date.getDate()).padStart(2, '0');
    return `${yyyy}-${mm}-${dd}`;
  };

  // Convert dd/mm/yyyy to YYYY-MM-DD
  const parseDateDDMMYYYY = (ddmmyyyy) => {
    if (!ddmmyyyy) return '';
    const parts = ddmmyyyy.split('/');
    if (parts.length !== 3) {
      // Try to parse as YYYY-MM-DD if it's not in dd/mm/yyyy format
      if (ddmmyyyy.match(/^\d{4}-\d{2}-\d{2}$/)) return ddmmyyyy;
      return '';
    }
    const day = parts[0].padStart(2, '0');
    const month = parts[1].padStart(2, '0');
    const year = parts[2];
    // Validate date
    const date = new Date(`${year}-${month}-${day}`);
    if (isNaN(date.getTime())) return '';
    return `${year}-${month}-${day}`;
  };

  // Convert YYYY-MM-DD to dd/mm/yyyy
  const formatDateDDMMYYYY = (isoDate) => {
    if (!isoDate) return '';
    const parts = isoDate.split('-');
    if (parts.length !== 3) return isoDate;
    return `${parts[2]}/${parts[1]}/${parts[0]}`;
  };

  // State for edit mode (double-click to type)
  const [startDateEditMode, setStartDateEditMode] = useState(false);
  const [endDateEditMode, setEndDateEditMode] = useState(false);
  const [startDateInputValue, setStartDateInputValue] = useState('');
  const [endDateInputValue, setEndDateInputValue] = useState('');
  
  // Custom input component for start date picker with double-click support
  const CustomStartDateInput = React.forwardRef(({ value, onClick, onChange, placeholder, disabled }, ref) => (
    <input
      ref={ref}
      type="text"
      value={value}
      onClick={onClick}
      onChange={onChange}
      placeholder={placeholder}
      disabled={disabled}
      onDoubleClick={handleStartDateDoubleClick}
      readOnly
      style={{
        background: 'white',
        border: '2px solid #1976d2',
        borderRadius: '6px',
        padding: '5px 9px',
        fontSize: '0.81rem',
        color: '#1976d2',
        fontWeight: 600,
        cursor: 'pointer',
        transition: 'all 0.3s ease',
        width: '100px',
        textAlign: 'center',
      }}
    />
  ));

  // Custom input component for end date picker with double-click support
  const CustomEndDateInput = React.forwardRef(({ value, onClick, onChange, placeholder, disabled }, ref) => (
    <input
      ref={ref}
      type="text"
      value={value}
      onClick={onClick}
      onChange={onChange}
      placeholder={placeholder}
      disabled={disabled}
      onDoubleClick={handleEndDateDoubleClick}
      readOnly
      style={{
        background: 'white',
        border: '2px solid #1976d2',
        borderRadius: '6px',
        padding: '5px 9px',
        fontSize: '0.81rem',
        color: '#1976d2',
        fontWeight: 600,
        cursor: 'pointer',
        transition: 'all 0.3s ease',
        width: '100px',
        textAlign: 'center',
      }}
    />
  ));

  // Sync input values when dates change (but not in edit mode)
  useEffect(() => {
    if (!startDateEditMode && startDate) {
      setStartDateInputValue(formatDateDDMMYYYY(startDate));
    }
  }, [startDate, startDateEditMode]);

  useEffect(() => {
    if (!endDateEditMode && endDate) {
      setEndDateInputValue(formatDateDDMMYYYY(endDate));
    }
  }, [endDate, endDateEditMode]);

  const handleStartDateChange = (date) => {
    if (date) {
      const isoDate = dateToIso(date);
      setStartDate(isoDate);
    setCurrentDayOffset(0); // Reset to first day when changing start date
      setStartDateEditMode(false);
    } else {
      setStartDate('');
      setStartDateEditMode(false);
    }
  };

  const handleStartDateInputChange = (e) => {
    const value = e.target.value;
    setStartDateInputValue(value);
    const isoDate = parseDateDDMMYYYY(value);
    if (isoDate) {
      setStartDate(isoDate);
      setCurrentDayOffset(0);
    }
  };

  const handleStartDateInputBlur = () => {
    setStartDateEditMode(false);
    if (startDate) {
      setStartDateInputValue(formatDateDDMMYYYY(startDate));
    } else {
      setStartDateInputValue('');
    }
  };

  const handleStartDateDoubleClick = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setStartDateEditMode(true);
    setStartDateInputValue(formatDateDDMMYYYY(startDate || getCurrentDayISO()));
  };

  const handleEndDateChange = (date) => {
    if (date) {
      const isoDate = dateToIso(date);
      setEndDate(isoDate);
      setEndDateEditMode(false);
    } else {
      setEndDate('');
      setEndDateEditMode(false);
    }
  };

  const handleEndDateInputChange = (e) => {
    const value = e.target.value;
    setEndDateInputValue(value);
    const isoDate = parseDateDDMMYYYY(value);
    if (isoDate) {
      setEndDate(isoDate);
    }
  };

  const handleEndDateInputBlur = () => {
    setEndDateEditMode(false);
    if (endDate) {
      setEndDateInputValue(formatDateDDMMYYYY(endDate));
    } else {
      setEndDateInputValue('');
    }
  };

  const handleEndDateDoubleClick = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setEndDateEditMode(true);
    setEndDateInputValue(formatDateDDMMYYYY(endDate));
  };

  // Navigation handlers for next/previous plant
  const handlePreviousPlant = () => {
    if (!onNavigateToPlant || plants.length === 0) return;
    const currentIndex = plants.findIndex(p => p.id === plant.id);
    if (currentIndex === -1) return;
    const previousIndex = currentIndex === 0 ? plants.length - 1 : currentIndex - 1;
    onNavigateToPlant(plants[previousIndex]);
  };

  const handleNextPlant = () => {
    if (!onNavigateToPlant || plants.length === 0) return;
    const currentIndex = plants.findIndex(p => p.id === plant.id);
    if (currentIndex === -1) return;
    const nextIndex = currentIndex === plants.length - 1 ? 0 : currentIndex + 1;
    onNavigateToPlant(plants[nextIndex]);
  };

  // Data filling function for CSV export
  const applyDataFilling = (records, numericColumns) => {
    if (records.length === 0) return records;
    
    // Create a copy to avoid mutating original data
    const filledRecords = records.map(row => ({ ...row }));
    
    // Get column keys for different data types
    // Use the dynamically detected keys
    const yesterdayForecastKey = 'forecasted_energy'; // Yesterday's Forecast
    const liveForecastKey = ['predicted_energy', 'expected_production', 'forecast', 'Forecast', 'prod_forecast']
      .find(k => numericColumns.includes(k));
    
    // Apply bfill and ffill for temperature column
    if (temperatureKey && numericColumns.includes(temperatureKey)) {
      // Forward fill (ffill) - fill forward with last known value
      let lastValidTemp = null;
      for (let i = 0; i < filledRecords.length; i++) {
        const temp = Number(filledRecords[i][temperatureKey]);
        if (Number.isFinite(temp) && temp !== null && temp !== undefined) {
          lastValidTemp = temp;
        } else if (lastValidTemp !== null) {
          filledRecords[i][temperatureKey] = lastValidTemp;
        }
      }
      
      // Backward fill (bfill) - fill backward with next known value
      let nextValidTemp = null;
      for (let i = filledRecords.length - 1; i >= 0; i--) {
        const temp = Number(filledRecords[i][temperatureKey]);
        if (Number.isFinite(temp) && temp !== null && temp !== undefined) {
          nextValidTemp = temp;
        } else if (nextValidTemp !== null) {
          filledRecords[i][temperatureKey] = nextValidTemp;
        }
      }
    }
    
    // Fill empty records with 0 for irradiance, yesterday's forecast, and live forecast
    const columnsToFillWithZero = [
      { key: irradianceKey, name: 'irradiance' },
      { key: yesterdayForecastKey, name: 'yesterday forecast' },
      { key: liveForecastKey, name: 'live forecast' }
    ];
    
    columnsToFillWithZero.forEach(({ key, name }) => {
      if (key) {
        // Check if the key exists in the data (either in numericColumns or in the actual records)
        const keyExists = numericColumns.includes(key) || 
                         (filledRecords.length > 0 && filledRecords[0].hasOwnProperty(key));
        
        if (keyExists) {
          filledRecords.forEach(row => {
            if (row.hasOwnProperty(key)) {
              const value = row[key];
              if (value === null || value === undefined || value === '' || 
                  (typeof value === 'number' && !Number.isFinite(value))) {
                row[key] = 0;
              }
            }
          });
        }
      }
    });
    
    return filledRecords;
  };

  const exportToCSV = () => {
    if (isExporting || rows.length === 0) return;
    
    setIsExporting(true);
    try {
      // Get the current filtered data (same as what's displayed on the plot)
      const filteredRows = getDateRangeData();
      if (filteredRows.length === 0) {
        setIsExporting(false);
        return;
      }

      // Create CSV headers based on toggle states and permissions
      const headers = ['timestamp'];
      
      if (showLiveForecast && hasPermission('live-intraday-forecast')) {
        const forecastKey = ['predicted_energy', 'expected_production', 'forecast', 'Forecast', 'prod_forecast']
          .find(k => numericColumns.includes(k));
        if (forecastKey) headers.push('INTRADAY_FORECAST_BETA');
      }
      
      if (showRealData && hasPermission('live-production')) {
        const realKey = [deltaKey || 'D.156.DELTA', 'production', 'real_production']
          .find(k => numericColumns.includes(k));
        if (realKey) headers.push('PRODUCTION');
      }
      
      if (showSolcastAggregate && hasPermission('live-day-ahead-forecast') && numericColumns.includes('forecasted_energy')) {
        headers.push('DAY_AHEAD_FORECAST');
      }
      
      if (showIrradiance && hasPermission('live-irradiance') && numericColumns.includes(irradianceKey)) {
        headers.push('IRRADIANCE');
      }
      
      if (showTemperature && hasPermission('live-temperature') && numericColumns.includes(temperatureKey)) {
        headers.push('TEMPERATURE');
      }

      // Create CSV content with timezone-based record trimming and prepending
      let recordsToProcess = filteredRows;
      let prependedRecords = [];
      
      // If EET or CET timezone is enabled, determine offset and trim records
      if ((timezone === 'EET' || timezone === 'CET') && filteredRows.length > 0) {
        // Get the first record to determine DST status
        const firstRow = filteredRows[0];
        const firstTimestamp = firstRow[timeAccessor];
        if (firstTimestamp) {
          const baseMs = parseTimeUTC(firstTimestamp);
          if (Number.isFinite(baseMs)) {
            const date = new Date(baseMs);
            const month = date.getUTCMonth();
            const day = date.getUTCDate();
            
            // Simple DST check: March 31 to October 31 (approximate)
            // EET is UTC+2/+3, CET is UTC+1/+2
            const isDST = (month > 2 && month < 9) || (month === 2 && day >= 31) || (month === 9 && day <= 31);
            const offsetHours = timezone === 'EET' ? (isDST ? 3 : 2) : (isDST ? 2 : 1);
            
            // Remove records based on offset: +3 hours = remove 12 records, +2 hours = remove 8 records, +1 hour = remove 4 records
            const recordsToRemove = offsetHours === 3 ? 12 : offsetHours === 2 ? 8 : 4;
            
            // Get the removed records from the end
            const removedRecords = filteredRows.slice(-recordsToRemove);
            
            // Create prepended records with -1 day timestamps
            prependedRecords = removedRecords.map(row => {
              const timestamp = row[timeAccessor];
              if (timestamp) {
                const baseMs = parseTimeUTC(timestamp);
                if (Number.isFinite(baseMs)) {
                  // Subtract 1 day (24 hours in milliseconds)
                  const oneDayMs = 24 * 60 * 60 * 1000;
                  const adjustedMs = baseMs - oneDayMs;
                  
                  // Create new row with adjusted timestamp
                  return {
                    ...row,
                    [timeAccessor]: new Date(adjustedMs).toISOString()
                  };
                }
              }
              return row;
            });
            
            // Keep the remaining records (remove the last ones)
            recordsToProcess = filteredRows.slice(0, Math.max(0, filteredRows.length - recordsToRemove));
          }
        }
      }
      
      // Combine prepended records (with -1 day) and main records
      const allRecordsToProcess = [...prependedRecords, ...recordsToProcess];
      
      // Apply data filling strategies
      const filledRecords = applyDataFilling(allRecordsToProcess, numericColumns);
      
      // Apply bfill (backward fill) for production column - fill ANY missing rows
      const realKey = [deltaKey || 'D.156.DELTA', 'production', 'real_production']
        .find(k => numericColumns.includes(k));
      
      if (realKey && numericColumns.includes(realKey)) {
        // Ensure all rows have the production key
        filledRecords.forEach(row => {
          if (!row.hasOwnProperty(realKey)) {
            row[realKey] = undefined;
          }
        });
        
        // Find the FIRST valid production value in the dataset
        let firstValidProduction = null;
        for (let i = 0; i < filledRecords.length; i++) {
          const prodValue = filledRecords[i][realKey];
          const prod = Number(prodValue);
          const isValid = Number.isFinite(prod) && 
                          prodValue !== null && 
                          prodValue !== undefined && 
                          prodValue !== '';
          if (isValid) {
            firstValidProduction = prod;
            break;
          }
        }
        
        // Fill all values before the first valid value with that value
        if (firstValidProduction !== null) {
          for (let i = 0; i < filledRecords.length; i++) {
            const prodValue = filledRecords[i][realKey];
            const prod = Number(prodValue);
            const isValid = Number.isFinite(prod) && 
                            prodValue !== null && 
                            prodValue !== undefined && 
                            prodValue !== '';
            if (!isValid) {
              filledRecords[i][realKey] = firstValidProduction;
            } else {
              // Once we hit a valid value, stop filling forward
              break;
            }
          }
        }
        
        // Backward fill (bfill) - fill backward with next known value from the end
        // Fill ANY missing/invalid value with the next known value
        let nextValidProduction = null;
        for (let i = filledRecords.length - 1; i >= 0; i--) {
          const prodValue = filledRecords[i][realKey];
          const prod = Number(prodValue);
          
          // Check if value is valid (including 0, but not empty string, null, or undefined)
          const isValid = Number.isFinite(prod) && 
                          prodValue !== null && 
                          prodValue !== undefined && 
                          prodValue !== '';
          
          if (isValid) {
            nextValidProduction = prod;
          } else {
            // Fill ANY missing/invalid value with next known production value
            if (nextValidProduction !== null) {
              filledRecords[i][realKey] = nextValidProduction;
            }
          }
        }
      }
      
      const csvContent = [
        headers.join(','),
        ...filledRecords.map(row => {
          // Handle timestamp conversion for UTC/RO
          let timestamp = row[timeAccessor] || '';
          if (timestamp && (timezone === 'EET' || timezone === 'CET')) {
            // Parse the timestamp and convert to timezone
            const baseMs = parseTimeUTC(timestamp);
            if (Number.isFinite(baseMs)) {
              const date = new Date(baseMs);
              const year = date.getUTCFullYear();
              const month = date.getUTCMonth();
              const day = date.getUTCDate();
              const hours = date.getUTCHours();
              const minutes = date.getUTCMinutes();
              const seconds = date.getUTCSeconds();
              
              // Simple DST check: March 31 to October 31 (approximate)
              // EET is UTC+2/+3, CET is UTC+1/+2
              const isDST = (month > 2 && month < 9) || (month === 2 && day >= 31) || (month === 9 && day <= 31);
              const offsetHours = timezone === 'EET' ? (isDST ? 3 : 2) : (isDST ? 2 : 1);
              
              // Add offset hours directly to UTC components
              let adjustedHours = hours + offsetHours;
              let adjustedDay = day;
              let adjustedMonth = month;
              let adjustedYear = year;
              
              // Handle day/month/year overflow
              if (adjustedHours >= 24) {
                adjustedHours -= 24;
                adjustedDay++;
                if (adjustedDay > new Date(year, month + 1, 0).getDate()) {
                  adjustedDay = 1;
                  adjustedMonth++;
                  if (adjustedMonth >= 12) {
                    adjustedMonth = 0;
                    adjustedYear++;
                  }
                }
              }
              
              const finalYear = adjustedYear;
              const finalMonth = String(adjustedMonth + 1).padStart(2, '0');
              const finalDay = String(adjustedDay).padStart(2, '0');
              const finalHours = String(adjustedHours).padStart(2, '0');
              const finalMinutes = String(minutes).padStart(2, '0');
              const finalSeconds = String(seconds).padStart(2, '0');
              timestamp = `${finalYear}-${finalMonth}-${finalDay} ${finalHours}:${finalMinutes}:${finalSeconds}`;
            }
          }
          const values = [timestamp];
          
          if (showLiveForecast && hasPermission('live-intraday-forecast')) {
            const forecastKey = ['predicted_energy', 'expected_production', 'forecast', 'Forecast', 'prod_forecast']
              .find(k => numericColumns.includes(k));
            const val = forecastKey ? (row[forecastKey] || 0) : 0;
            values.push(val);
          }
          
          if (showRealData && hasPermission('live-production')) {
            const realKey = [deltaKey || 'D.156.DELTA', 'production', 'real_production']
              .find(k => numericColumns.includes(k));
            // Check if value is a valid number (including 0), otherwise use empty string
            let val = '';
            if (realKey && row.hasOwnProperty(realKey)) {
              const prodValue = row[realKey];
              const prod = Number(prodValue);
              if (Number.isFinite(prod) && prodValue !== null && prodValue !== undefined && prodValue !== '') {
                val = prod;
              }
            }
            values.push(val);
          }
          
          if (showSolcastAggregate && hasPermission('live-day-ahead-forecast') && numericColumns.includes('forecasted_energy')) {
            const val = row['forecasted_energy'] || 0;
            values.push(val);
          }
          
          if (showIrradiance && hasPermission('live-irradiance') && numericColumns.includes(irradianceKey)) {
            const val = row[irradianceKey] || 0;
            values.push(val);
          }
          
          if (showTemperature && hasPermission('live-temperature') && numericColumns.includes(temperatureKey)) {
            const val = row[temperatureKey] || 0;
            values.push(val);
          }
          
          return values.join(',');
        })
      ].join('\n');

      // Create filename with plant code, date and time
      const now = new Date();
      const dateStr = now.toISOString().split('T')[0]; // YYYY-MM-DD
      const timeStr = now.toTimeString().split(' ')[0].replace(/:/g, '-'); // HH-MM-SS
      const filename = `${plant.id}_${dateStr}_${timeStr}.csv`;

      // Create and download the file
      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const link = document.createElement('a');
      const url = URL.createObjectURL(blob);
      link.setAttribute('href', url);
      link.setAttribute('download', filename);
      link.style.visibility = 'hidden';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      
    } catch (error) {
      // Error exporting CSV
    } finally {
      setIsExporting(false);
    }
  };

  return (
    <PageContainer>
      {isRefreshing && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.7)',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 10000,
          color: 'white',
          fontSize: '1.5rem',
          fontWeight: 600
        }}>
          <RefreshSpinner />
          <div style={{ marginBottom: '10px', fontSize: '1.5rem', fontWeight: 600 }}>
            Refreshing data...
          </div>
          {refreshMessage && (
            <div style={{ 
              marginBottom: '10px', 
              fontSize: '1.1rem', 
              color: '#fff',
              fontWeight: 400,
              textAlign: 'center',
              minHeight: '30px',
              transition: 'opacity 0.3s ease'
            }}>
              {refreshMessage}
            </div>
          )}
          <div style={{ 
            fontSize: '0.9rem', 
            color: '#ccc',
            marginTop: '10px'
          }}>
            Elapsed: {Math.floor(refreshElapsed / 60)}:{(refreshElapsed % 60).toString().padStart(2, '0')}
          </div>
          <div style={{ fontSize: '0.85rem', color: '#999', marginTop: '5px' }}>
            This may take up to 2 minutes
          </div>
        </div>
      )}
      <MainContent>
        <ContentWrapper>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '10px', padding: '0 16px' }}>
            <Title>FORECAST {plant.name}</Title>
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
              {lastUpdated && typeof lastUpdated.utcYear === 'number' && typeof lastUpdated.utcMonth === 'number' && typeof lastUpdated.utcDay === 'number' ? (
                <>
                  <LastUpdated>
                    <span>Updated:</span>
                    <span style={{ fontWeight: 700, color: 'white' }}>
                      {(() => {
                        // Convert UTC components to selected timezone
                        const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
                        if (lastUpdated.utcMonth < 1 || lastUpdated.utcMonth > 12) return 'Invalid date';
                        
                        // Get UTC components
                        let year = lastUpdated.utcYear;
                        let month = lastUpdated.utcMonth;
                        let day = lastUpdated.utcDay;
                        let hour = lastUpdated.utcHour !== undefined ? lastUpdated.utcHour : 0;
                        let minute = lastUpdated.utcMinute !== undefined ? lastUpdated.utcMinute : 0;
                        
                        // Apply timezone offset
                        let timezoneLabel = 'UTC';
                        if (timezone === 'CET') {
                          hour += 1; // CET is UTC+1
                          timezoneLabel = 'CET';
                          // Handle day/month/year rollover
                          if (hour >= 24) {
                            hour -= 24;
                            day += 1;
                            // Check if day exceeds month length (simplified - doesn't handle month/year boundaries perfectly)
                            const daysInMonth = [31, 28, 31, 30, 31, 30, 31, 31, 30, 31, 30, 31];
                            if (day > daysInMonth[month - 1]) {
                              day = 1;
                              month += 1;
                              if (month > 12) {
                                month = 1;
                                year += 1;
                              }
                            }
                          }
                        } else if (timezone === 'EET') {
                          hour += 2; // EET is UTC+2
                          timezoneLabel = 'EET';
                          // Handle day/month/year rollover
                          if (hour >= 24) {
                            hour -= 24;
                            day += 1;
                            // Check if day exceeds month length (simplified - doesn't handle month/year boundaries perfectly)
                            const daysInMonth = [31, 28, 31, 30, 31, 30, 31, 31, 30, 31, 30, 31];
                            if (day > daysInMonth[month - 1]) {
                              day = 1;
                              month += 1;
                              if (month > 12) {
                                month = 1;
                                year += 1;
                              }
                            }
                          }
                        }
                        
                        const monthName = months[month - 1];
                        const hourStr = String(hour).padStart(2, '0');
                        const minuteStr = String(minute).padStart(2, '0');
                        return `${monthName} ${day}, ${hourStr}:${minuteStr} ${timezoneLabel}`;
                      })()}
                    </span>
                  </LastUpdated>
                  <RefreshIconButton
                onClick={async () => {
                  setIsRefreshing(true);
                  try {
                    // Get current timestamp before refresh
                    const beforeRefresh = lastUpdated ? {
                      utcYear: lastUpdated.utcYear,
                      utcMonth: lastUpdated.utcMonth,
                      utcDay: lastUpdated.utcDay,
                      utcHour: lastUpdated.utcHour,
                      utcMinute: lastUpdated.utcMinute,
                      utcSecond: lastUpdated.utcSecond
                    } : null;
                    
                    // Call the backend endpoint which proxies to Azure Function
                    const response = await authenticatedFetch(
                      `/api/plants/${plant.id}/refresh`,
                      {
                        method: 'POST',
                        headers: {
                          'Content-Type': 'application/json'
                        }
                      }
                    );
                    
                    if (response.ok) {
                      // Poll for timestamp change - check every 2 seconds, max 5 minutes
                      const maxAttempts = 150; // 5 minutes = 150 * 2 seconds
                      let attempts = 0;
                      
                      const checkForUpdate = async () => {
                        try {
                          const lastUpdatedResp = await authenticatedFetch(`/api/plants/${plant.id}/last-updated`);
                          if (lastUpdatedResp.ok) {
                            const lastUpdatedData = await lastUpdatedResp.json();
                            if (lastUpdatedData.success && lastUpdatedData.utcYear && lastUpdatedData.utcMonth !== undefined && lastUpdatedData.utcDay !== undefined) {
                              const newTimestamp = {
                                utcYear: lastUpdatedData.utcYear,
                                utcMonth: lastUpdatedData.utcMonth,
                                utcDay: lastUpdatedData.utcDay,
                                utcHour: lastUpdatedData.utcHour || 0,
                                utcMinute: lastUpdatedData.utcMinute || 0,
                                utcSecond: lastUpdatedData.utcSecond || 0
                              };
                              
                              // Check if timestamp has changed
                              if (!beforeRefresh || 
                                  newTimestamp.utcYear !== beforeRefresh.utcYear ||
                                  newTimestamp.utcMonth !== beforeRefresh.utcMonth ||
                                  newTimestamp.utcDay !== beforeRefresh.utcDay ||
                                  newTimestamp.utcHour !== beforeRefresh.utcHour ||
                                  newTimestamp.utcMinute !== beforeRefresh.utcMinute ||
                                  newTimestamp.utcSecond !== beforeRefresh.utcSecond) {
                                // Timestamp changed - update state and stop polling
                                setLastUpdated(newTimestamp);
                                setIsRefreshing(false);
                                return;
                              }
                            }
                          }
                          
                          // If no change yet, continue polling
                          attempts++;
                          if (attempts < maxAttempts) {
                            setTimeout(checkForUpdate, 2000); // Check again in 2 seconds
                          } else {
                            // Timeout - stop polling
                            setIsRefreshing(false);
                          }
                        } catch (err) {
                          attempts++;
                          if (attempts < maxAttempts) {
                            setTimeout(checkForUpdate, 2000);
                          } else {
                            setIsRefreshing(false);
                          }
                        }
                      };
                      
                      // Start polling after a short delay
                      setTimeout(checkForUpdate, 2000);
                    } else {
                      setIsRefreshing(false);
                    }
                  } catch (err) {
                    setIsRefreshing(false);
                  }
                }}
                disabled={isRefreshing}
                title={isRefreshing ? 'Refreshing...' : 'Refresh data'}
              >
                <FiRefreshCw size={18} style={{ transform: isRefreshing ? 'rotate(360deg)' : 'none', transition: isRefreshing ? 'transform 1s linear infinite' : 'none' }} />
              </RefreshIconButton>
                </>
              ) : null}
            </div>
          </div>
          
          {currentInvertersDown > 0 && (
            <div style={{ 
              textAlign: 'center', 
              marginTop: '-10px', 
              marginBottom: '10px',
              color: '#d32f2f',
              fontWeight: 700,
              fontSize: '1.1rem'
            }}>
              {currentInvertersDown} Inverter{currentInvertersDown !== 1 ? 's' : ''} Down
            </div>
          )}

          {loading ? (
            <LoadingProgress currentStep={loadingStep} totalSteps={3} />
          ) : error ? (
            <ErrorBox>{error}</ErrorBox>
          ) : rows.length === 0 ? (
            <div>No live data available.</div>
          ) : (
            <>
              <NavigationContainer>
                <NavButton 
                  onClick={handlePreviousDay} 
                  disabled={isRefreshing || (!startDate && !endDate ? (currentDayOffset === 0) : false)}
                >
                  <FiChevronLeft size={18} />
                </NavButton>
                
                <DatePickerContainer>
                  <DateLabel>Start Date</DateLabel>
                  <StyledDatePicker>
                    {startDateEditMode ? (
                      <input
                        type="text"
                        value={startDateInputValue}
                        onChange={handleStartDateInputChange}
                        onBlur={handleStartDateInputBlur}
                        placeholder="dd/mm/yyyy"
                        disabled={isRefreshing}
                        autoFocus
                        style={{
                          background: 'white',
                          border: '2px solid #1976d2',
                          borderRadius: '6px',
                          padding: '5px 9px',
                          fontSize: '0.81rem',
                          color: '#1976d2',
                          fontWeight: 600,
                          cursor: 'text',
                          transition: 'all 0.3s ease',
                          width: '100px',
                          textAlign: 'center',
                          outline: 'none',
                        }}
                      />
                    ) : (
                  <DatePicker
                        selected={isoToDate(startDate || getCurrentDayISO())}
                    onChange={handleStartDateChange}
                        dateFormat="dd/MM/yyyy"
                        placeholderText="dd/mm/yyyy"
                    disabled={isRefreshing}
                        showPopperArrow={false}
                        customInput={<CustomStartDateInput />}
                  />
                    )}
                  </StyledDatePicker>
                  <DateLabel>End Date</DateLabel>
                  <StyledDatePicker>
                    {endDateEditMode ? (
                      <input
                        type="text"
                        value={endDateInputValue}
                        onChange={handleEndDateInputChange}
                        onBlur={handleEndDateInputBlur}
                        placeholder="dd/mm/yyyy"
                        disabled={isRefreshing}
                        autoFocus
                        style={{
                          background: 'white',
                          border: '2px solid #1976d2',
                          borderRadius: '6px',
                          padding: '5px 9px',
                          fontSize: '0.81rem',
                          color: '#1976d2',
                          fontWeight: 600,
                          cursor: 'text',
                          transition: 'all 0.3s ease',
                          width: '100px',
                          textAlign: 'center',
                          outline: 'none',
                        }}
                      />
                    ) : (
                  <DatePicker
                        selected={isoToDate(endDate)}
                    onChange={handleEndDateChange}
                        dateFormat="dd/MM/yyyy"
                        placeholderText="dd/mm/yyyy"
                    disabled={isRefreshing}
                        showPopperArrow={false}
                        customInput={<CustomEndDateInput />}
                  />
                    )}
                  </StyledDatePicker>
                
                  
                  <SegmentedToggle>
                    <SegmentedOption 
                      $active={timezone === 'UTC'} 
                      onClick={() => !isRefreshing && setTimezone('UTC')}
                      style={{ pointerEvents: isRefreshing ? 'none' : 'auto', opacity: isRefreshing ? 0.5 : 1 }}
                    >
                      UTC
                    </SegmentedOption>
                    <SegmentedOption 
                      $active={timezone === 'CET'} 
                      onClick={() => !isRefreshing && setTimezone('CET')}
                      style={{ pointerEvents: isRefreshing ? 'none' : 'auto', opacity: isRefreshing ? 0.5 : 1 }}
                    >
                      CET
                    </SegmentedOption>
                    <SegmentedOption 
                      $active={timezone === 'EET'} 
                      onClick={() => !isRefreshing && setTimezone('EET')}
                      style={{ pointerEvents: isRefreshing ? 'none' : 'auto', opacity: isRefreshing ? 0.5 : 1 }}
                    >
                      EET
                    </SegmentedOption>
                  </SegmentedToggle>
                  
                  <NavButton 
                    onClick={exportToCSV}
                    disabled={isRefreshing || isExporting || rows.length === 0}
                    style={{ marginLeft: '10px', padding: '4px 10px', fontSize: '0.7rem' }}
                  >
                    {isExporting ? 'Exporting...' : 'Save CSV'}
                  </NavButton>
                </DatePickerContainer>
                
                <NavButton 
                  onClick={handleNextDay}
                  disabled={isRefreshing || (!startDate && !endDate ? (currentDayOffset >= totalDays - 1) : false)}
                >
                  <FiChevronRight size={18} />
                </NavButton>
              </NavigationContainer>

              <ChartContainer>
                {/* MAE and WAPE metrics for single day view - Day-Ahead Forecast */}
                {(() => {
                  const isSingleDay = startDate && endDate && startDate === endDate;
                  if (!isSingleDay || !deltaKey) return null;
                  if (!showSolcastAggregate || !hasPermission('live-day-ahead-forecast')) return null;
                  
                  const dayData = getDateRangeData();
                  if (dayData.length === 0) return null;
                  
                  // Calculate MAE and WAPE for day-ahead forecast vs production
                  // Use forecasted_energy (day-ahead forecast) vs production (deltaKey)
                  const pairs = dayData
                    .map(row => {
                      // Day-ahead forecast is forecasted_energy
                      const forecast = row['forecasted_energy'];
                      const forecastNum = (forecast !== undefined && forecast !== null && forecast !== '') 
                        ? Number(forecast) 
                        : NaN;
                      
                      // Production is the deltaKey column
                      const production = row[deltaKey];
                      const productionNum = (production !== undefined && production !== null && production !== '') 
                        ? Number(production) 
                        : NaN;
                      
                      return { forecast: forecastNum, production: productionNum };
                    })
                    .filter(p => 
                      Number.isFinite(p.forecast) && 
                      Number.isFinite(p.production) && 
                      p.production > 0 // Only include rows with production > 0 (standard for WAPE)
                    );
                  
                  if (pairs.length === 0) return null;
                  
                  // MAE = mean(|forecast - production|)
                  const mae = pairs.reduce((sum, p) => sum + Math.abs(p.forecast - p.production), 0) / pairs.length;
                  
                  // WAPE = sum(|forecast - production|) / sum(production) * 100
                  const sumAbsDiff = pairs.reduce((sum, p) => sum + Math.abs(p.forecast - p.production), 0);
                  const sumProduction = pairs.reduce((sum, p) => sum + p.production, 0);
                  const wape = sumProduction > 0 ? (sumAbsDiff / sumProduction) * 100 : 0;
                  
                  return (
                    <MetricsContainer>
                      <MetricsTitle>Day-Ahead Forecast</MetricsTitle>
                      <MetricRow>
                        <MetricLabel>MAE:</MetricLabel>
                        <MetricValueWrapper
                          onMouseEnter={() => setHoveredMetric('mae-day')}
                          onMouseLeave={() => setHoveredMetric(null)}
                        >
                          <MetricValue>{mae.toFixed(3)} MWh</MetricValue>
                          <MetricTooltip $show={hoveredMetric === 'mae-day'}>
                            Mean Absolute Error: Average of absolute differences between forecast and actual production values.
                          </MetricTooltip>
                        </MetricValueWrapper>
                      </MetricRow>
                      <MetricRow>
                        <MetricLabel>WAPE:</MetricLabel>
                        <MetricValueWrapper
                          onMouseEnter={() => setHoveredMetric('wape-day')}
                          onMouseLeave={() => setHoveredMetric(null)}
                        >
                          <MetricValue>{wape.toFixed(2)}%</MetricValue>
                          <MetricTooltip $show={hoveredMetric === 'wape-day'}>
                            Weighted Absolute Percentage Error: Sum of absolute errors divided by sum of actual production, expressed as a percentage.
                          </MetricTooltip>
                        </MetricValueWrapper>
                      </MetricRow>
                    </MetricsContainer>
                  );
                })()}
                {/* MAE and WAPE metrics for single day view - Intraday Forecast */}
                {(() => {
                  const isSingleDay = startDate && endDate && startDate === endDate;
                  if (!isSingleDay || !deltaKey) return null;
                  if (!showLiveForecast || !hasPermission('live-intraday-forecast')) return null;
                  
                  const dayData = getDateRangeData();
                  if (dayData.length === 0) return null;
                  
                  // Find the intraday forecast key (predicted_energy or fallback)
                  // Check if any of the forecast keys exist in the data
                  const forecastKey = ['predicted_energy', 'expected_production', 'forecast', 'Forecast', 'prod_forecast']
                    .find(k => dayData.some(row => row[k] !== undefined && row[k] !== null && row[k] !== ''));
                  
                  if (!forecastKey) return null;
                  
                  // Calculate MAE and WAPE for intraday forecast vs production
                  // Use predicted_energy (intraday forecast) vs production (deltaKey)
                  const pairs = dayData
                    .map(row => {
                      // Intraday forecast is predicted_energy (or fallback)
                      const forecast = row[forecastKey];
                      const forecastNum = (forecast !== undefined && forecast !== null && forecast !== '') 
                        ? Number(forecast) 
                        : NaN;
                      
                      // Production is the deltaKey column
                      const production = row[deltaKey];
                      const productionNum = (production !== undefined && production !== null && production !== '') 
                        ? Number(production) 
                        : NaN;
                      
                      return { forecast: forecastNum, production: productionNum };
                    })
                    .filter(p => 
                      Number.isFinite(p.forecast) && 
                      Number.isFinite(p.production) && 
                      p.production > 0 // Only include rows with production > 0 (standard for WAPE)
                    );
                  
                  if (pairs.length === 0) return null;
                  
                  // MAE = mean(|forecast - production|)
                  const mae = pairs.reduce((sum, p) => sum + Math.abs(p.forecast - p.production), 0) / pairs.length;
                  
                  // WAPE = sum(|forecast - production|) / sum(production) * 100
                  const sumAbsDiff = pairs.reduce((sum, p) => sum + Math.abs(p.forecast - p.production), 0);
                  const sumProduction = pairs.reduce((sum, p) => sum + p.production, 0);
                  const wape = sumProduction > 0 ? (sumAbsDiff / sumProduction) * 100 : 0;
                  
                  return (
                    <MetricsContainer style={{ top: showSolcastAggregate && hasPermission('live-day-ahead-forecast') ? '140px' : '10px' }}>
                      <MetricsTitle>Intraday Forecast</MetricsTitle>
                      <MetricRow>
                        <MetricLabel>MAE:</MetricLabel>
                        <MetricValueWrapper
                          onMouseEnter={() => setHoveredMetric('mae-intraday')}
                          onMouseLeave={() => setHoveredMetric(null)}
                        >
                          <MetricValue>{mae.toFixed(3)} MWh</MetricValue>
                          <MetricTooltip $show={hoveredMetric === 'mae-intraday'}>
                            Mean Absolute Error: Average of absolute differences between forecast and actual production values.
                          </MetricTooltip>
                        </MetricValueWrapper>
                      </MetricRow>
                      <MetricRow>
                        <MetricLabel>WAPE:</MetricLabel>
                        <MetricValueWrapper
                          onMouseEnter={() => setHoveredMetric('wape-intraday')}
                          onMouseLeave={() => setHoveredMetric(null)}
                        >
                          <MetricValue>{wape.toFixed(2)}%</MetricValue>
                          <MetricTooltip $show={hoveredMetric === 'wape-intraday'}>
                            Weighted Absolute Percentage Error: Sum of absolute errors divided by sum of actual production, expressed as a percentage.
                          </MetricTooltip>
                        </MetricValueWrapper>
                      </MetricRow>
                    </MetricsContainer>
                  );
                })()}
                <svg width={width} height={height} style={{ display: 'block', margin: 0, background: 'white' }}>
                  {/* Solid white background to ensure plot area is white regardless of page background */}
                  <rect x="0" y="0" width={width} height={height} fill="white" />
                  {/* NOW vertical marker */}
                  {seriesData.length > 0 && (() => {
                    const s = seriesData[0];
                    const now = Date.now(); // milliseconds UTC epoch
                    if (now >= s.tMin && now <= s.tMax) {
                      const x = s.xScale(now);
                      return (
                        <g>
                          <line x1={x} x2={x} y1={padding.top} y2={height - padding.bottom} stroke="#ef4444" strokeWidth="2" strokeDasharray="4,4" />
                          {/* Label next to the line (to the right), no time */}
                          <rect x={x + 6} y={padding.top + 4} width="38" height="16" fill="#ef4444" rx="3" ry="3" />
                          <text x={x + 25} y={padding.top + 16} textAnchor="middle" fontSize="11" fill="#ffffff" fontWeight="700">NOW</text>
                        </g>
                      );
                    }
                    return null;
                  })()}
                  {/* Maintenance periods visualization */}
                  {seriesData.length > 0 && downtimeRecords.length > 0 && (() => {
                    const s = seriesData[0];
                    const visibleRecords = downtimeRecords
                      .filter(record => record.endMs > s.tMin && record.startMs < s.tMax)
                      .map((record, idx) => {
                        const startX = Math.max(s.xScale(s.tMin), s.xScale(record.startMs));
                        const endX = Math.min(s.xScale(s.tMax), s.xScale(record.endMs));
                        const rectWidth = Math.max(0, endX - startX);
                        return { ...record, startX, endX, rectWidth, idx };
                      })
                      .filter(r => r.rectWidth > 0);
                    
                    return visibleRecords.map((record, idx) => {
                        const midX = record.startX + record.rectWidth / 2;
                        const labelY = padding.top + 20 + (idx * 25); // Stack labels vertically
                        
                        return (
                          <g key={`maintenance-${record.idx}`}>
                            {/* Highlighted area */}
                            <rect
                              x={record.startX}
                              y={padding.top}
                              width={record.rectWidth}
                              height={height - padding.top - padding.bottom}
                              fill="#ffa726"
                              opacity={0.2}
                            />
                            {/* Start vertical line */}
                            <line
                              x1={record.startX}
                              x2={record.startX}
                              y1={padding.top}
                              y2={height - padding.bottom}
                              stroke="#facc15"
                              strokeWidth="2"
                            />
                            {/* End vertical line */}
                            <line
                              x1={record.endX}
                              x2={record.endX}
                              y1={padding.top}
                              y2={height - padding.bottom}
                              stroke="#facc15"
                              strokeWidth="2"
                            />
                            {/* Label showing number of inverters */}
                            <rect
                              x={midX - 70}
                              y={labelY}
                              width="140"
                              height="20"
                              fill="#facc15"
                              opacity={0.9}
                              rx="4"
                            />
                            <text
                              x={midX}
                              y={labelY + 14}
                              textAnchor="middle"
                              fontSize="11"
                              fill="#ffffff"
                              fontWeight="700"
                            >
                              {maxInverters > 0 ? (maxInverters - record.inverters_running) : record.inverters_running} Inverter{maxInverters > 0 && (maxInverters - record.inverters_running) !== 1 ? 's' : ''} Down
                            </text>
                          </g>
                        );
                      });
                  })()}
                  {seriesData.map(s => (
                    <g key={s.key}>
                      {s.bars.map((bar, idx) => (
                        <rect
                          className="forecast-bar"
                          key={idx}
                          x={bar.x}
                          y={bar.y}
                          width={bar.width}
                          height={bar.height}
                          fill={(s.isReal ? showRealData : showLiveForecast) ? s.color : 'transparent'}
                          stroke="transparent"
                          strokeWidth="0"
                          rx="2"
                          opacity={(s.isReal ? showRealData : showLiveForecast) ? (hoveredBar === `${s.key}-${idx}` ? 0.95 : 0.85) : 0}
                          onMouseEnter={() => setHoveredBar(`${s.key}-${idx}`)}
                          onMouseLeave={() => setHoveredBar(null)}
                          style={{ 
                            cursor: (s.isReal ? showRealData : showLiveForecast) ? 'pointer' : 'default', 
                            pointerEvents: (s.isReal ? showRealData : showLiveForecast) ? 'auto' : 'none'
                          }}
                        />
                      ))}
                    </g>
                  ))}
                  
                  {/* Irradiance, Temperature, and Solcast Aggregate line series */}
                  {lineSeriesData.map(ls => (
                    ((ls.key === irradianceKey && showIrradiance && hasPermission('live-irradiance')) || 
                     (ls.key === temperatureKey && showTemperature && hasPermission('live-temperature')) || 
                     (ls.key === 'forecasted_energy' && showSolcastAggregate && hasPermission('live-day-ahead-forecast'))) ? (
                      <g key={`line-group-${ls.key}`}>
                      <polyline
                        key={`line-${ls.key}`}
                        fill="none"
                        stroke={ls.color}
                        strokeWidth="2"
                        points={ls.points.map(p => `${p.x},${p.y}`).join(' ')}
                        opacity="0.9"
                          style={{ cursor: 'pointer' }}
                          onMouseMove={(e) => {
                            // Throttle hover detection for performance
                            const target = e.currentTarget;
                            if (!target || !target.dataset) return;
                            if (target.dataset.hovering) return;
                            target.dataset.hovering = 'true';
                            
                            const clientX = e.clientX;
                            const clientY = e.clientY;
                            
                            requestAnimationFrame(() => {
                              if (!target || !target.dataset) return;
                              target.dataset.hovering = '';
                              // Find closest point on line to mouse position
                              const svg = target.closest('svg');
                              if (!svg) return;
                              try {
                                const pt = svg.createSVGPoint();
                                pt.x = clientX;
                                pt.y = clientY;
                                const svgP = pt.matrixTransform(svg.getScreenCTM().inverse());
                                
                                let closestIdx = 0;
                                let minDist = Infinity;
                                // Only check every 3rd point for performance
                                for (let idx = 0; idx < ls.points.length; idx += 3) {
                                  const p = ls.points[idx];
                                  const dist = Math.sqrt(Math.pow(p.x - svgP.x, 2) + Math.pow(p.y - svgP.y, 2));
                                  if (dist < minDist && dist < 20) {
                                    minDist = dist;
                                    closestIdx = idx;
                                  }
                                }
                                
                                if (minDist < 20) {
                                  setHoveredLine(`${ls.key}-${closestIdx}`);
                                }
                              } catch (err) {
                                // Silently fail if SVG transform fails
                              }
                            });
                          }}
                          onMouseLeave={() => setHoveredLine(null)}
                        />
                        {/* Add visible points for day-ahead forecast line */}
                        {ls.key === 'forecasted_energy' && ls.points.map((p, idx) => (
                          <circle
                            key={`point-${ls.key}-${idx}`}
                            cx={p.x}
                            cy={p.y}
                            r="4.5"
                            fill={ls.color}
                            stroke="white"
                            strokeWidth="1.5"
                            opacity="0.9"
                            style={{ cursor: 'pointer' }}
                            onMouseMove={() => setHoveredLine(`${ls.key}-${idx}`)}
                            onMouseLeave={() => setHoveredLine(null)}
                          />
                        ))}
                      </g>
                    ) : null
                  ))}

                  {/* X-axis timestamps (labels only, no vertical lines) */}
                  {seriesData.length > 0 && (() => {
                    // Always use the fixed day range (00:00 to 24:00)
                    const minTime = seriesData[0].tMin;
                    const maxTime = seriesData[0].tMax;

                    const labels = [];
                    const startHour = new Date(minTime);
                    startHour.setUTCMinutes(0, 0, 0);
                    const oneHourMs = 60 * 60 * 1000;
                    const totalHours = Math.ceil((maxTime - minTime) / oneHourMs);
                    
                    // Dynamic interval: more days = bigger intervals
                    let intervalHours = 1;
                    // Keep every timestamp up to 3 days; then 2h up to 1 week; beyond that 4h
                    if (totalHours <= 24 * 3) {
                      intervalHours = 1;
                    } else if (totalHours <= 24 * 7) {
                      intervalHours = 2;
                    } else {
                      intervalHours = 4;
                    }
                    
                    let cur = new Date(startHour);
                    while (cur.getTime() <= maxTime) {
                      const hourTime = cur.getTime();
                      if (hourTime >= minTime) {
                        labels.push({
                          time: hourTime,
                          label: cur.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit', hour12: false, timeZone: timezone === 'EET' ? 'Europe/Bucharest' : timezone === 'CET' ? 'Europe/Rome' : 'UTC' })
                        });
                      }
                      cur.setUTCHours(cur.getUTCHours() + intervalHours);
                    }

                    return labels.map((marker, idx) => {
                      const x = seriesData[0].xScale(marker.time);
                      return (
                        <text
                          key={idx}
                          x={x}
                          y={height - padding.bottom + 25}
                          textAnchor="middle"
                          fontSize={Math.max(10, Math.min(16, Math.floor(10 + (totalHours / 24) * 2)))}
                          fill="#333"
                          fontWeight="600"
                          transform={`rotate(-45 ${x} ${height - padding.bottom + 25})`}
                        >
                          {marker.label}
                        </text>
                      );
                    });
                  })()}
                  
                  {/* Y-axis labels only (grid lines removed) - use same maxValue as plot */}
                  {seriesData.length > 0 && (() => {
                    const s = seriesData[0];
                    // Use the same vMax as the plot (which uses actualMaxInData for scaling)
                    const minValue = 0;
                    const maxValue = s.vMax; // This is globalVMax which uses actualMaxInData
                    const stepSize = 0.1;
                    const steps = Math.round((maxValue - minValue) / stepSize);
                    
                    // Create yScale for labels that maps from 0 to maxValue (same as plot yScale)
                    const labelYScale = (v) => {
                      const labelMin = 0;
                      const labelMax = maxValue;
                      // Use same calculation as plot yScale to ensure alignment
                      const range = labelMax - labelMin;
                      return height - padding.bottom - ((v - labelMin) / Math.max(0.01, range)) * (height - padding.top - padding.bottom);
                    };
                    
                    return Array.from({ length: steps + 1 }, (_, i) => {
                      const value = minValue + (stepSize * i);
                      const y = labelYScale(value);
                      return (
                        <g key={i}>
                          <text
                            x={padding.left - 20}
                            y={y + 5}
                            textAnchor="end"
                            fontSize="12"
                            fill="#333"
                            fontWeight="600"
                          >
                            {value.toFixed(1)}
                          </text>
                        </g>
                      );
                    });
                  })()}
                  
                  {/* Y-axis unit label */}
                  <text
                    x={padding.left - 45}
                    y={height / 2}
                    textAnchor="middle"
                    fontSize="12"
                    fill="#333"
                    fontWeight="600"
                    transform={`rotate(-90 ${padding.left - 45} ${height / 2})`}
                  >
                    MWh
                  </text>

                  {/* Enhanced tooltip on top of everything - show all plotted features */}
                  {hoveredBar && (() => {
                    const [seriesKey, barIdx] = hoveredBar.split('-');
                    const series = seriesData.find(s => s.key === seriesKey);
                    if (!series) return null;
                    const index = parseInt(barIdx);
                    const bar = series.bars[index];
                    const timestamp = series.timestamps[index];
                    const baseMs = parseTimeUTC(timestamp);
                    let displayMs = baseMs;
                    if (timezone !== 'UTC') {
                      const timeZoneMap = { 'EET': 'Europe/Bucharest', 'CET': 'Europe/Rome' };
                      displayMs = baseMs + getTimeZoneOffsetMs(baseMs, timeZoneMap[timezone] || 'UTC');
                    }

                    // Find the matching raw row by exact UTC timestamp
                    const rowMatch = rows.find(r => {
                      const v = r[timeAccessor];
                      if (!v) return false;
                      return parseTimeUTC(v) === baseMs;
                    }) || {};

                    
                    // Identify keys locally for tooltip (case-insensitive, with seriesData fallback)
                    const lowerCols = columns.map(c => String(c).toLowerCase());
                    const pickKey = (candidates) => candidates.find(k => lowerCols.includes(String(k).toLowerCase()));
                    let forecastKey = pickKey(['predicted_energy','expected_production','forecast','Forecast','prod_forecast']);
                    let realKey = pickKey([deltaKey || 'D.156.DELTA','production','real_production']);
                    if (!forecastKey) {
                      const sForecast = seriesData.find(s => !s.isReal);
                      if (sForecast) forecastKey = sForecast.key;
                    }
                    if (!realKey) {
                      const sReal = seriesData.find(s => s.isReal);
                      if (sReal) realKey = sReal.key;
                    }
                    

                    
                    const entries = [];
                    // Build entries from plotted series to avoid key mismatches
                    seriesData.forEach(s2 => {
                      const shouldShow = s2.isReal ? showRealData : showLiveForecast;
                      if (!shouldShow) return;
                      const v = Number(rowMatch[s2.key]);
                      if (Number.isFinite(v)) {
                        entries.push({
                          label: s2.isReal ? 'PRODUCTION' : 'FORECAST',
                          value: `${v.toFixed(3)} MWh`,
                          color: s2.isReal ? 'rgba(239, 68, 68, 1)' : 'rgba(16, 185, 129, 1)'
                        });
                      }
                    });
                    if (showSolcastAggregate && columns.includes('forecasted_energy')) {
                      const v = Number(rowMatch['forecasted_energy']);
                      if (Number.isFinite(v)) entries.push({ label: 'DAY-AHEAD FORECAST', value: `${v.toFixed(3)} MWh`, color: '#6b7280' });
                    }

                    // Add irradiance and temperature at the bottom
                    if (showIrradiance && columns.includes(irradianceKey)) {
                      const v = Number(rowMatch[irradianceKey]);
                      if (Number.isFinite(v)) entries.push({ label: 'IRRADIANCE', value: v.toFixed(0), color: '#f59e0b' });
                    }
                    if (showTemperature && columns.includes(temperatureKey)) {
                      const v = Number(rowMatch[temperatureKey]);
                      if (Number.isFinite(v)) entries.push({ label: 'TEMPERATURE', value: `${v.toFixed(1)}°C`, color: '#3b82f6' });
                    }

                    const timeStr = new Date(displayMs).toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', hour12: false, timeZone: 'UTC' });
                    const dateStr = new Date(displayMs).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric', timeZone: 'UTC' });

                    const lineHeight = 14;
                    const headerHeight = 18;
                    const entryGap = 4;
                    const paddingY = 8;
                    const minWidth = 160;
                    const maxWidth = Math.max(200, width - padding.left - padding.right - 8);
                    const headerText = `${dateStr} ${timeStr}`;
                    const entryMaxLen = entries.reduce((m, e) => Math.max(m, String(e.label + ': ' + e.value).length), 0);
                    const headerLen = headerText.length;
                    const charPx = 8;
                    const contentWidth = Math.max(headerLen * charPx + 20, entryMaxLen * charPx + 20);
                    const boxWidth = Math.min(maxWidth, Math.max(minWidth, contentWidth));
                    const numEntries = Math.max(1, entries.length);
                    const boxHeight = headerHeight + paddingY + (numEntries * (lineHeight + entryGap));
                    let boxX = bar.x - boxWidth / 2;
                    let boxY = Math.max(10, bar.y - (boxHeight + 10));
                    const plotBottom = height - padding.bottom;
                    boxY = Math.min(boxY, plotBottom - boxHeight - 4);
                    boxY = Math.max(padding.top + 4, boxY);
                    boxX = Math.min(Math.max(boxX, padding.left + 4), width - boxWidth - 4);

                    return (
                      <g>
                        <rect
                          x={boxX}
                          y={boxY}
                          width={boxWidth}
                          height={boxHeight}
                          fill="rgba(0,0,0,0.9)"
                          rx="8"
                          ry="8"
                          stroke={series.color.replace('0.7)', '1)').replace('0.6)', '1)')}
                          strokeWidth="2"
                        />
                        <text x={boxX + boxWidth/2} y={boxY + (headerHeight - 4)} textAnchor="middle" fontSize="11" fill="#fff" fontWeight="600">
                          {dateStr} {timeStr}
                        </text>
                        {entries.map((e, i) => (
                          <text
                            key={i}
                            x={boxX + 10}
                            y={boxY + headerHeight + (paddingY/2) + (i+1) * lineHeight}
                            fontSize="12"
                            fill={e.color}
                            fontWeight="600"
                          >
                            {e.label}: {e.value}
                          </text>
                        ))}
                      </g>
                    );
                  })()}
                  
                  {/* Tooltip for line hovers */}
                  {hoveredLine && (() => {
                    const [lineKey, pointIdx] = hoveredLine.split('-');
                    const lineSeries = lineSeriesData.find(ls => ls.key === lineKey);
                    if (!lineSeries || !lineSeries.points[parseInt(pointIdx)]) return null;
                    
                    const point = lineSeries.points[parseInt(pointIdx)];
                    const filteredRows = getDateRangeData();
                    const times = filteredRows.map(r => parseTimeUTC(r[timeAccessor]));
                    
                    // Find the closest timestamp to this point
                    let closestTime = null;
                    let closestIdx = -1;
                    let minDist = Infinity;
                    
                    if (seriesData.length > 0) {
                      const ref = seriesData[0];
                      times.forEach((t, idx) => {
                        if (Number.isFinite(t)) {
                          const x = ref.xScale(t);
                          const dist = Math.abs(x - point.x);
                          if (dist < minDist) {
                            minDist = dist;
                            closestTime = t;
                            closestIdx = idx;
                          }
                        }
                      });
                    }
                    
                    if (closestIdx === -1 || !closestTime) return null;
                    
                    const baseMs = closestTime;
                    let displayMs = baseMs;
                    if (timezone !== 'UTC') {
                      const timeZoneMap = { 'EET': 'Europe/Bucharest', 'CET': 'Europe/Rome' };
                      displayMs = baseMs + getTimeZoneOffsetMs(baseMs, timeZoneMap[timezone] || 'UTC');
                    }
                    
                    const rowMatch = filteredRows[closestIdx] || {};
                    const entries = [];
                    
                    // Get the value for this line
                    const lineValue = Number(rowMatch[lineKey]);
                    if (Number.isFinite(lineValue)) {
                      let label = '';
                      let color = lineSeries.color;
                      if (lineKey === 'forecasted_energy') {
                        label = 'DAY-AHEAD FORECAST';
                        color = '#6b7280';
                        entries.push({ label, value: `${lineValue.toFixed(3)} MWh`, color });
                      } else if (lineKey === irradianceKey) {
                        label = 'IRRADIANCE';
                        entries.push({ label, value: lineValue.toFixed(0), color });
                      } else if (lineKey === temperatureKey) {
                        label = 'TEMPERATURE';
                        entries.push({ label, value: `${lineValue.toFixed(1)}°C`, color });
                      }
                    }
                    
                    // Also show bar values if available
                    seriesData.forEach(s2 => {
                      const shouldShow = s2.isReal ? showRealData : showLiveForecast;
                      if (!shouldShow) return;
                      const v = Number(rowMatch[s2.key]);
                      if (Number.isFinite(v)) {
                        entries.push({
                          label: s2.isReal ? 'PRODUCTION' : 'FORECAST',
                          value: `${v.toFixed(3)} MWh`,
                          color: s2.isReal ? 'rgba(239, 68, 68, 1)' : 'rgba(16, 185, 129, 1)'
                        });
                      }
                    });
                    
                    // Add other line values
                    if (lineKey !== 'forecasted_energy' && showSolcastAggregate && hasPermission('live-day-ahead-forecast') && columns.includes('forecasted_energy')) {
                      const v = Number(rowMatch['forecasted_energy']);
                      if (Number.isFinite(v)) entries.push({ label: 'DAY-AHEAD FORECAST', value: `${v.toFixed(3)} MWh`, color: '#6b7280' });
                    }
                    if (lineKey !== irradianceKey && showIrradiance && hasPermission('live-irradiance') && columns.includes(irradianceKey)) {
                      const v = Number(rowMatch[irradianceKey]);
                      if (Number.isFinite(v)) entries.push({ label: 'IRRADIANCE', value: v.toFixed(0), color: '#f59e0b' });
                    }
                    if (lineKey !== temperatureKey && showTemperature && hasPermission('live-temperature') && columns.includes(temperatureKey)) {
                      const v = Number(rowMatch[temperatureKey]);
                      if (Number.isFinite(v)) entries.push({ label: 'TEMPERATURE', value: `${v.toFixed(1)}°C`, color: '#3b82f6' });
                    }
                    
                    const timeStr = new Date(displayMs).toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', hour12: false, timeZone: 'UTC' });
                    const dateStr = new Date(displayMs).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric', timeZone: 'UTC' });
                    
                    const lineHeight = 14;
                    const headerHeight = 18;
                    const entryGap = 4;
                    const paddingY = 8;
                    const minWidth = 160;
                    const maxWidth = Math.max(200, width - padding.left - padding.right - 8);
                    const headerText = `${dateStr} ${timeStr}`;
                    const entryMaxLen = entries.reduce((m, e) => Math.max(m, String(e.label + ': ' + e.value).length), 0);
                    const headerLen = headerText.length;
                    const charPx = 8;
                    const contentWidth = Math.max(headerLen * charPx + 20, entryMaxLen * charPx + 20);
                    const boxWidth = Math.min(maxWidth, Math.max(minWidth, contentWidth));
                    const numEntries = Math.max(1, entries.length);
                    const boxHeight = headerHeight + paddingY + (numEntries * (lineHeight + entryGap));
                    let boxX = point.x - boxWidth / 2;
                    let boxY = Math.max(10, point.y - (boxHeight + 10));
                    const plotBottom = height - padding.bottom;
                    boxY = Math.min(boxY, plotBottom - boxHeight - 4);
                    boxY = Math.max(padding.top + 4, boxY);
                    boxX = Math.min(Math.max(boxX, padding.left + 4), width - boxWidth - 4);
                    
                    return (
                      <g>
                        <rect
                          x={boxX}
                          y={boxY}
                          width={boxWidth}
                          height={boxHeight}
                          fill="rgba(0,0,0,0.9)"
                          rx="8"
                          ry="8"
                          stroke={lineSeries.color}
                          strokeWidth="2"
                        />
                        <text x={boxX + boxWidth/2} y={boxY + (headerHeight - 4)} textAnchor="middle" fontSize="11" fill="#fff" fontWeight="600">
                          {dateStr} {timeStr}
                        </text>
                        {entries.map((e, i) => (
                          <text
                            key={i}
                            x={boxX + 10}
                            y={boxY + headerHeight + (paddingY/2) + (i+1) * lineHeight}
                            fontSize="12"
                            fill={e.color}
                            fontWeight="600"
                          >
                            {e.label}: {e.value}
                          </text>
                        ))}
                      </g>
                    );
                  })()}
                </svg>
              </ChartContainer>

              <ButtonsBar>
                <ToggleContainerWrapper>
                  {plants.length > 1 && onNavigateToPlant && (
                    <SmallToggleButton 
                      onClick={handlePreviousPlant}
                      disabled={isRefreshing}
                      color="#1976d2"
                      style={{ transform: 'scale(0.625)', transformOrigin: 'center', position: 'absolute', left: '20px' }}
                    >
                      <FiChevronLeft size={14} />
                      PREVIOUS PLANT
                    </SmallToggleButton>
                  )}
                  <ToggleContainer>
                    <SmallToggleButton 
                      $active={showSolcastAggregate}
                      onClick={() => setShowSolcastAggregate(!showSolcastAggregate)}
                      color="#6b7280"
                      disabled={!hasPermission('live-day-ahead-forecast')}
                    >
                      DAY-AHEAD FORECAST
                    </SmallToggleButton>
                    <ToggleButton 
                      $active={showLiveForecast}
                      onClick={() => setShowLiveForecast(!showLiveForecast)}
                      $isReal={false}
                      disabled={!hasPermission('live-intraday-forecast')}
                    >
                      INTRADAY FORECAST (BETA)
                    </ToggleButton>
                    <ToggleButton 
                      $active={showRealData}
                      onClick={() => setShowRealData(!showRealData)}
                      $isReal={true}
                      disabled={!hasPermission('live-production')}
                    >
                      PRODUCTION
                    </ToggleButton>
                    <SmallToggleButton 
                      $active={showIrradiance}
                      onClick={() => setShowIrradiance(!showIrradiance)}
                      color="#f59e0b"
                      disabled={!hasPermission('live-irradiance')}
                    >
                      IRRADIANCE
                    </SmallToggleButton>
                    <SmallToggleButton 
                      $active={showTemperature}
                      onClick={() => setShowTemperature(!showTemperature)}
                      color="#3b82f6"
                      disabled={!hasPermission('live-temperature')}
                    >
                      TEMPERATURE
                    </SmallToggleButton>
                  </ToggleContainer>
                  {plants.length > 1 && onNavigateToPlant && (
                    <SmallToggleButton 
                      onClick={handleNextPlant}
                      disabled={isRefreshing}
                      color="#1976d2"
                      style={{ transform: 'scale(0.625)', transformOrigin: 'center', position: 'absolute', right: '20px' }}
                    >
                      NEXT PLANT
                      <FiChevronRight size={14} />
                    </SmallToggleButton>
                  )}
                </ToggleContainerWrapper>
              </ButtonsBar>
            </>
          )}
        </ContentWrapper>
      </MainContent>
    </PageContainer>
  );
};

export default MeteoblueLivePage;


